package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.ChecklistAlojamentoCatalog
import br.com.htms.inspeccaocampo.data.ChecklistVetoresCatalog
import br.com.htms.inspeccaocampo.data.FormChecklistAlojamento
import br.com.htms.inspeccaocampo.data.FormComunicacaoOcorrencias
import br.com.htms.inspeccaocampo.data.FormRelatorioFoto
import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.data.FotoAnexo
import br.com.htms.inspeccaocampo.data.InspecaoCompleta
import br.com.htms.inspeccaocampo.data.ItemConformidade
import br.com.htms.inspeccaocampo.data.RelatorioFotoCatalog
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.data.RespostaTernaria
import br.com.htms.inspeccaocampo.data.checklist.AxiaRequisitosMev
import br.com.htms.inspeccaocampo.platform.PlatformImage
import br.com.htms.inspeccaocampo.platform.PlatformServices
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr
import br.com.htms.inspeccaocampo.util.ImageHeaders
import br.com.htms.inspeccaocampo.platform.ImageSize
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min
import kotlinx.coroutines.yield

/**
 * PDF no mesmo padrão visual do APK ([PdfExporter] + [PdfLayout]):
 * cabeçalho logo+banner, tabelas com borda, assinaturas em caixa, 1 página por foto.
 */
object JpegPdfBuilder {
    private const val PAGE_W = 595.0
    private const val PAGE_H = 842.0
    private const val MARGIN = 40.0
    private val CONTENT_W get() = PAGE_W - MARGIN * 2
    private const val COL1_RATIO = 0.36

    /** Gravado no /Info do PDF — fingerprint forense de qual build gerou o arquivo. */
    const val PDF_BUILD_ID = "20260915sigvis1"

    /** Fundo A4 (stretch) para PDFs padrão — não usado em Axia diário. */
    private const val PAGE_BG_ASSET = "pl_fundo_pdf.jpg"

    /** Logo HTMS do [PAGE_BG_ASSET] ocupa até ~57pt do topo (canto direito). */
    private const val PAGE_BG_TOP_MARGIN = 68.0

    /** Fundo A4 sem logo no canto — Checklist Diário / Inspeção HTMS. */
    private const val PAGE_BG_HTMS_CHECKLIST = "pl_fundo_pdf_sem_logo.jpg"

    /**
     * Navy dominante do banner.png do usuário (#021B3D) — fundo sob alpha ao
     * converter PNG→JPEG no PDF (evita contorno branco).
     */
    private const val BANNER_FLATTEN_RGB = 0x021B3D

    /** Margem do PDF Checklist Diário HTMS (igual ao APK ChecklistPdfExporter). */
    private const val CD_MARGIN = 28.0

    /**
     * Geometria do PDF oficial Axia MEV 3.26 (A4), medida no arquivo de referência:
     * Check List … Mobilizados - MEV 3.26 (1) - Copia.pdf
     *
     * O modelo oficial desenha a grade com retângulos PRETOS PREENCHIDOS (~0.48pt),
     * não com `re S` (traço fino some na borda direita em vários viewers).
     */
    private const val AXIA_X0 = 92.4
    private const val AXIA_X1 = 503.28
    private val AXIA_CONTENT_W = AXIA_X1 - AXIA_X0
    /** Separação rótulo | valor (modelo oficial). */
    private const val AXIA_X_ROTULO = 166.56
    private const val AXIA_X_MEIO = 306.72
    private const val AXIA_X_ROTULO_DIR = 424.32
    private const val AXIA_COL_ITENS_W = 32.6
    private const val AXIA_COL_C_W = 14.9
    private const val AXIA_COL_NC_W = 14.8
    private const val AXIA_COL_NA_W = 14.9
    private const val AXIA_COL_OBS_W = 34.1
    private const val AXIA_PAD = 3.4
    private const val AXIA_LINE = 0.48
    private const val AXIA_STROKE = 0.48
    private const val AXIA_HEADER_ROW_H = 12.2
    private const val AXIA_TITLE_H = 45.12
    private const val AXIA_LEGENDA_H = 54.0
    private const val AXIA_TABLE_HEADER_H = 12.4
    private const val AXIA_ITEM_ROW_H = 15.6
    private const val AXIA_ITEM_ROW_H_WRAP = 23.5
    private const val AXIA_ITEM_ROW_H_COMPACT = 14.4
    private const val AXIA_ITEM_ROW_H_WRAP_COMPACT = 21.5
    private const val AXIA_OBS_H = 28.0
    /**
     * Faixa única Aprovado | Reprovado | Assinatura — mesma altura do
     * retângulo vermelho (rótulo + checkbox + tinta da assinatura).
     */
    private const val AXIA_SIG_H = 22.0
    private const val AXIA_TOP_MARGIN = 54.0
    /** Logo MEV 3.26: bbox (469.44, 54.72) 33.12×43.92. */
    private const val AXIA_LOGO_X = 469.44
    private const val AXIA_LOGO_W = 33.12
    private const val AXIA_LOGO_H = 43.92
    private const val AXIA_LOGO_TOP_INSET = 0.72
    /** Ícone voltar MEV 3.26: bbox (91.44, 53.76) 18.96×18.48. */
    private const val AXIA_ICON_X = 91.44
    private const val AXIA_ICON_W = 18.96
    private const val AXIA_ICON_H = 18.48
    private const val AXIA_ICON_TOP_INSET = -0.24
    private const val AXIA_COL_APROV_W = 114.0
    private const val AXIA_COL_REPROV_W = 167.0
    /** Verde oficial "# Classificação: Pública". */
    private const val AXIA_CLASS_R = 7.0 / 255.0
    private const val AXIA_CLASS_G = 131.0 / 255.0
    private const val AXIA_CLASS_B = 7.0 / 255.0
    private const val AXIA_CLASS_Y = 14.0

    private data class PageDesc(
        val contentStream: String,
        val imageIndices: List<Int> = emptyList()
    )

    /** Índices 0-based referenciados por `/ImN Do` no content stream. */
    internal fun imageIndicesFromContent(contentStream: String): List<Int> =
        Regex("""/Im(\d+)\s+Do""")
            .findAll(contentStream)
            .map { it.groupValues[1].toInt() - 1 }
            .filter { it >= 0 }
            .distinct()
            .toList()

    suspend fun build(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.form
        val layout = LayoutEngine()
        layout.setPageBackground(loadHeaderJpeg(PAGE_BG_ASSET), PAGE_BG_TOP_MARGIN)

        // Inspeção de campo diária: banner (JPEG preferido; PNG com flatten navy).
        val bannerJpeg = loadHeaderJpeg("banner.jpg")
            ?: loadHeaderJpeg("banner.png", flattenRgb = BANNER_FLATTEN_RGB)
        layout.drawHeader(logo = null, banner = bannerJpeg)

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            buildList {
                add("TST Responsável" to f.tstResponsavel)
                add("Data e Horário" to f.dataHorarioFormatado())
                add("Obra" to f.obra.ifBlank { "-" })
                add("Efetivo" to f.efetivo)
                add("Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' '))
                f.linkGoogleMaps()?.let { add("Google Maps" to it) }
                add("Responsável Frente" to f.responsavelFrente)
                add("Descrição da Atividade" to f.descricaoAtividade)
            }
        )

        layout.drawSectionTitle("Verificações de campo")
        layout.drawTable(
            f.itens.map { item ->
                val resp = item.resposta?.rotulo ?: "-"
                val valor = buildString {
                    append("Resposta: $resp")
                    if (item.resposta == RespostaTernaria.NAO || item.resposta == RespostaTernaria.NA) {
                        if (item.justificativa.isNotBlank()) {
                            append("\nJustificativa: ${item.justificativa}")
                        }
                    }
                }
                item.titulo to valor
            }
        )

        if (f.itensExtra.any { it.temConteudo() }) {
            layout.drawSectionTitle("Itens extras")
            layout.drawTable(
                f.itensExtra.mapIndexedNotNull { index, p ->
                    if (!p.temConteudo()) return@mapIndexedNotNull null
                    val valor = buildString {
                        append("Resposta: ${p.resposta?.rotulo ?: "-"}")
                        if (p.titulo.isNotBlank()) append("\nNome: ${p.titulo}")
                        if (p.justificativa.isNotBlank()) {
                            append("\nJustificativa: ${p.justificativa}")
                        }
                    }
                    p.rotuloSequencia(index) to valor
                }
            )
        }

        layout.drawSectionTitle("Desvios, ações e conclusão")
        layout.drawTable(
            listOf(
                "Desvios Encontrados" to f.desviosEncontrados.textoExportacao(),
                "Ações Corretivas" to f.acoesCorretivas.textoExportacao(),
                "Informações adicionais" to f.informacoesAdicionais.ifBlank { "—" },
                "Conclusão" to f.conclusao
            )
        )

        layout.drawSectionTitle("Assinaturas")
        layout.drawSignature("SUPERVISOR", f.assinaturaSupervisor)
        layout.drawSignature("SESMT", f.assinaturaSesmt)
        layout.drawFooter()
        yield()

        f.fotosDetalhadasParaExportacao().forEach { item ->
            val resolved = resolveFotoJpeg(item.foto)
            layout.drawAnexoFotoPage(
                secao = item.secao,
                resposta = item.resposta,
                foto = item.foto,
                formLat = f.latitude,
                formLng = f.longitude,
                resolved = resolved
            )
            yield()
        }

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    suspend fun buildRelatorio(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.relatorioFoto ?: FormRelatorioFoto()
        val layout = LayoutEngine()
        layout.setPageBackground(loadHeaderJpeg(PAGE_BG_ASSET), PAGE_BG_TOP_MARGIN)

        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logo = null, banner = bannerJpeg)

        layout.drawSectionTitle("Legenda")
        layout.drawTable(listOf("Opções" to RespostaConformidade.LEGENDA_SIM_NAO))

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            buildList {
                add("Responsável pelo Alojamento" to f.responsavelAlojamento)
                add("Endereço" to f.endereco)
                add("Data início contrato" to f.dataInicioContrato)
                add("Obra" to f.obra.ifBlank { "-" })
                add("Data e Horário" to f.dataHorarioFormatado())
                add("Capacidade total de pessoas" to f.capacidadeTotalPessoas)
                add("Quantidade de funcionários" to f.quantidadeFuncionarios)
                add("Quantidade de quartos" to f.quantidadeQuartos)
                add("Quantidade de camas" to f.quantidadeCamas)
                add("Quantidade de armários" to f.quantidadeArmarios)
                add("Quantidade de banheiros" to f.quantidadeBanheiros)
                add("Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' '))
                if (f.latitude != null && f.longitude != null) {
                    add("Google Maps" to "https://maps.google.com/?q=${f.latitude},${f.longitude}")
                }
            }
        )

        RelatorioFotoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            layout.drawSectionTitle(secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to textoItemConformidade(item, usarSimNao = true)
                }
            )
        }

        layout.drawSectionTitle(RelatorioFotoCatalog.SECAO_OBSERVACOES)
        layout.drawTable(
            listOf("Observações" to f.observacoesGerais.ifBlank { "—" })
        )

        layout.drawFooter()

        f.fotosDetalhadasParaExportacao().forEach { item ->
            val resolved = resolveFotoJpeg(item.foto)
            layout.drawAnexoFotoPage(
                secao = item.secao,
                resposta = item.resposta,
                foto = item.foto,
                formLat = f.latitude,
                formLng = f.longitude,
                resolved = resolved
            )
        }

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    suspend fun buildChecklist(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.checklistAlojamento ?: FormChecklistAlojamento()
        val vetores = inspecao.formTipo == FormTipo.CHECKLIST_VETORES
        val layout = LayoutEngine()
        layout.setPageBackground(loadHeaderJpeg(PAGE_BG_ASSET), PAGE_BG_TOP_MARGIN)

        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logo = null, banner = bannerJpeg)

        layout.drawSectionTitle(if (vetores) ChecklistVetoresCatalog.TITULO else "Legenda")
        layout.drawTable(
            listOf(
                "Conformidade" to if (vetores) {
                    "Conforme (foto) / Não conforme (foto) / N/A (justificativa)"
                } else {
                    RespostaConformidade.LEGENDA
                }
            )
        )

        layout.drawSectionTitle(if (vetores) "1. Identificação" else "Dados gerais")
        layout.drawTable(
            if (vetores) {
                listOf(
                    "Obra" to f.obra.ifBlank { "-" },
                    "Local do alojamento" to f.endereco.ifBlank { "-" },
                    "Responsável pela inspeção" to f.inspecionadoPor.nome.ifBlank { "-" },
                    "Data" to f.dataHorarioFormatado(),
                    "Número de trabalhadores" to f.numeroTrabalhadores.ifBlank { "-" },
                    "Responsável pelo alojamento" to f.acompanhadoPor.nome.ifBlank { "-" },
                    "Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' ')
                )
            } else {
                listOf(
                    "Endereço" to f.endereco,
                    "Obra" to f.obra.ifBlank { "-" },
                    "Data e Horário" to f.dataHorarioFormatado(),
                    "Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' ')
                )
            }
        )

        val secoes = if (vetores) ChecklistVetoresCatalog.secoes() else ChecklistAlojamentoCatalog.secoesPadrao()
        secoes.forEachIndexed { indice, secao ->
            val itensSecao = f.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEachIndexed
            layout.drawSectionTitle(if (vetores) "${indice + 2}. ${secao.titulo}" else secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to if (vetores) textoItemVetores(item) else textoItemConformidade(item)
                }
            )
        }

        if (vetores) {
            layout.drawSectionTitle("8. Orientações para tratamento")
            layout.drawTable(ChecklistVetoresCatalog.orientacoes)
        } else {
            layout.drawSectionTitle("Liberado para uso")
            layout.drawTable(
                listOf("Resposta" to (f.liberadoParaUso?.rotulo ?: "-"))
            )
        }

        if (f.acoesCorrecao.isNotEmpty()) {
            layout.drawSectionTitle(if (vetores) "7. Plano de ação" else "Ações de correção")
            layout.drawTable(
                f.acoesCorrecao.mapIndexed { index, acao ->
                    "Ação ${index + 1}" to buildString {
                        if (acao.item.isNotBlank()) append("Item: ${acao.item}\n")
                        if (acao.acao.isNotBlank()) append("Ação: ${acao.acao}\n")
                        if (acao.responsavel.isNotBlank()) append("Responsável: ${acao.responsavel}\n")
                        if (acao.prazo.isNotBlank()) append("Prazo: ${acao.prazo}")
                    }.trim()
                }
            )
        }

        layout.drawSectionTitle(if (vetores) "9. Aprovação" else "Assinaturas")
        if (vetores) {
            layout.drawTable(
                listOf(
                    "Responsável pela inspeção" to assinaturaTexto(f.inspecionadoPor.nome, f.inspecionadoPor.data),
                    "Responsável pelo alojamento" to assinaturaTexto(f.acompanhadoPor.nome, f.acompanhadoPor.data),
                    "Data da verificação das correções" to f.dataVerificacao.ifBlank { "-" }
                )
            )
            layout.drawSignature("Responsável pela inspeção", f.inspecionadoPor.assinatura)
            layout.drawSignature("Assinatura", f.acompanhadoPor.assinatura)
        } else {
            layout.drawTable(
                listOf(
                    "Inspecionado por" to assinaturaTexto(f.inspecionadoPor.nome, f.inspecionadoPor.data),
                    "Acompanhado por" to assinaturaTexto(f.acompanhadoPor.nome, f.acompanhadoPor.data),
                    "Engenheiro residente" to assinaturaTexto(f.engenheiroResidente.nome, f.engenheiroResidente.data)
                )
            )
            layout.drawSignature("Inspecionado por", f.inspecionadoPor.assinatura)
            layout.drawSignature("Acompanhado por", f.acompanhadoPor.assinatura)
            layout.drawSignature("Engenheiro residente", f.engenheiroResidente.assinatura)
        }
        layout.drawFooter()

        if (vetores) {
            f.itens.forEach { item ->
                val resposta = when (item.resposta) {
                    RespostaConformidade.C -> "Conforme"
                    RespostaConformidade.NC -> "Não conforme"
                    RespostaConformidade.NA -> "N/A"
                    null -> "—"
                }
                item.fotos.forEach { foto ->
                    layout.drawAnexoFotoPage(
                        secao = "${item.secao} — ${item.tituloNumerado}",
                        resposta = resposta,
                        foto = foto,
                        formLat = f.latitude,
                        formLng = f.longitude,
                        resolved = resolveFotoJpeg(foto)
                    )
                }
            }
        }

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    /** PDF único: Parte 1 (fotográfico) + Parte 2 (checklist), mesmo mold HTMS. */
    suspend fun buildRelatorioAlojamento(inspecao: InspecaoCompleta): ByteArray {
        val f1 = inspecao.relatorioFoto ?: FormRelatorioFoto()
        val f2 = inspecao.checklistAlojamento ?: FormChecklistAlojamento()
        val layout = LayoutEngine()
        layout.setPageBackground(loadHeaderJpeg(PAGE_BG_ASSET), PAGE_BG_TOP_MARGIN)

        val bannerJpeg = loadHeaderJpeg("topo_banner.jpg")
        layout.drawHeader(logo = null, banner = bannerJpeg)

        layout.drawSectionTitle("Relatório Alojamento — Parte 1 (Fotográfico)")
        layout.drawTable(listOf("Opções" to RespostaConformidade.LEGENDA_SIM_NAO))

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            buildList {
                add("Responsável pelo Alojamento" to f1.responsavelAlojamento)
                add("Endereço" to f1.endereco)
                add("Data início contrato" to f1.dataInicioContrato)
                add("Obra" to f1.obra.ifBlank { "-" })
                add("Data e Horário" to f1.dataHorarioFormatado())
                add("Capacidade total de pessoas" to f1.capacidadeTotalPessoas)
                add("Quantidade de funcionários" to f1.quantidadeFuncionarios)
                add("Quantidade de quartos" to f1.quantidadeQuartos)
                add("Quantidade de camas" to f1.quantidadeCamas)
                add("Quantidade de armários" to f1.quantidadeArmarios)
                add("Quantidade de banheiros" to f1.quantidadeBanheiros)
                add("Local (GPS)" to f1.localTexto.replace('\n', ' ').replace('\r', ' '))
                if (f1.latitude != null && f1.longitude != null) {
                    add("Google Maps" to "https://maps.google.com/?q=${f1.latitude},${f1.longitude}")
                }
            }
        )

        RelatorioFotoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f1.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            layout.drawSectionTitle(secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to textoItemConformidade(item, usarSimNao = true)
                }
            )
        }

        layout.drawSectionTitle(RelatorioFotoCatalog.SECAO_OBSERVACOES)
        layout.drawTable(
            listOf("Observações" to f1.observacoesGerais.ifBlank { "—" })
        )
        layout.drawFooter()

        f1.fotosDetalhadasParaExportacao().forEach { item ->
            val resolved = resolveFotoJpeg(item.foto)
            layout.drawAnexoFotoPage(
                secao = "Parte 1 — ${item.secao}",
                resposta = item.resposta,
                foto = item.foto,
                formLat = f1.latitude,
                formLng = f1.longitude,
                resolved = resolved
            )
        }

        layout.newPage()
        layout.drawHeader(logo = null, banner = bannerJpeg)
        layout.drawSectionTitle("Relatório Alojamento — Parte 2 (Checklist)")
        layout.drawTable(listOf("Conformidade" to RespostaConformidade.LEGENDA))

        layout.drawSectionTitle("Dados gerais")
        layout.drawTable(
            listOf(
                "Endereço" to f2.endereco,
                "Obra" to f2.obra.ifBlank { "-" },
                "Data e Horário" to f2.dataHorarioFormatado(),
                "Local (GPS)" to f2.localTexto.replace('\n', ' ').replace('\r', ' ')
            )
        )

        ChecklistAlojamentoCatalog.secoesPadrao().forEach { secao ->
            val itensSecao = f2.itens.filter { it.secao == secao.titulo }
            if (itensSecao.isEmpty()) return@forEach
            layout.drawSectionTitle(secao.titulo)
            layout.drawTable(
                itensSecao.map { item ->
                    item.tituloNumerado to textoItemConformidade(item)
                }
            )
        }

        layout.drawSectionTitle("Liberado para uso")
        layout.drawTable(
            listOf("Resposta" to (f2.liberadoParaUso?.rotulo ?: "-"))
        )

        if (f2.acoesCorrecao.isNotEmpty()) {
            layout.drawSectionTitle("Ações de correção")
            layout.drawTable(
                f2.acoesCorrecao.mapIndexed { index, acao ->
                    "Ação ${index + 1}" to buildString {
                        if (acao.item.isNotBlank()) append("Item: ${acao.item}\n")
                        if (acao.acao.isNotBlank()) append("Ação: ${acao.acao}\n")
                        if (acao.responsavel.isNotBlank()) append("Responsável: ${acao.responsavel}\n")
                        if (acao.prazo.isNotBlank()) append("Prazo: ${acao.prazo}")
                    }.trim()
                }
            )
        }

        layout.drawSectionTitle("Assinaturas")
        layout.drawTable(
            listOf(
                "Inspecionado por" to assinaturaTexto(f2.inspecionadoPor.nome, f2.inspecionadoPor.data),
                "Acompanhado por" to assinaturaTexto(f2.acompanhadoPor.nome, f2.acompanhadoPor.data),
                "Engenheiro residente" to assinaturaTexto(f2.engenheiroResidente.nome, f2.engenheiroResidente.data)
            )
        )
        layout.drawSignature("Inspecionado por", f2.inspecionadoPor.assinatura)
        layout.drawSignature("Acompanhado por", f2.acompanhadoPor.assinatura)
        layout.drawSignature("Engenheiro residente", f2.engenheiroResidente.assinatura)
        layout.drawFooter()

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    suspend fun buildComunicacao(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.comunicacaoOcorrencias ?: FormComunicacaoOcorrencias()
        val layout = LayoutEngine()
        layout.setPageBackground(loadHeaderJpeg(PAGE_BG_ASSET), PAGE_BG_TOP_MARGIN)

        val logoJpeg = loadHeaderJpeg("Logo-HTMS-pdf.png")
            ?: loadHeaderJpeg("logo_htms.png")
        // Comunicação Prévia de ocorrências: só logo HTMS no cabeçalho (sem banner).
        layout.drawHeader(logoJpeg, banner = null)

        layout.drawSectionTitle("Comunicação Prévia de ocorrências")
        layout.drawTable(
            listOf(
                "Descrição" to "Acidente, incidentes e quase acidentes"
            )
        )

        layout.drawSectionTitle("Dados da obra")
        layout.drawTable(
            buildList {
                add("Cliente" to f.cliente)
                add("Nome da obra" to f.obra.ifBlank { "-" })
                add("Data/hora registro" to f.dataHorarioFormatado())
                add("Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' '))
                f.linkGoogleMaps()?.let { add("Google Maps" to it) }
            }
        )

        layout.drawSectionTitle("Caracterização")
        layout.drawTable(
            listOf(
                "Caracterização" to (f.caracterizacao?.rotulo ?: "-"),
                "Tipo" to (f.tipo?.rotulo ?: "-")
            )
        )

        layout.drawSectionTitle("Identificação do evento")
        layout.drawTable(
            listOf(
                "Data e hora do ocorrido" to f.dataHoraOcorrido,
                "Local exato" to f.localExato,
                "Quem está reportando" to f.quemReportando,
                "Tipo de evento" to (f.tipoEvento?.rotulo ?: "-")
            )
        )

        layout.drawSectionTitle("Dados do Envolvido")
        layout.drawTable(
            listOf(
                "Nome" to f.envolvidoNome,
                "Função" to f.envolvidoFuncao
            )
        )

        layout.drawSectionTitle("Responsável pela atividade")
        layout.drawTable(
            listOf(
                "Responsável pela atividade" to f.responsavelAtividade,
                "Cargo" to f.responsavelCargo,
                "Estava no local" to (f.responsavelEstavaNoLocal?.rotulo ?: "-")
            )
        )

        layout.drawSectionTitle("Dados da Ocorrência")
        layout.drawTable(
            listOf(
                "Data do acidente" to f.dataAcidente,
                "Hora do acidente" to f.horaAcidente,
                "Horas trabalhadas" to f.horasTrabalhadas,
                "Local do acidente" to f.localAcidente,
                "Parte do corpo lesionada" to f.parteCorpoLesionada,
                "Agente causador da lesão" to f.agenteCausador,
                "Natureza da lesão" to f.naturezaLesao
            )
        )

        layout.drawSectionTitle("Ações tomadas de imediato")
        layout.drawTable(
            listOf("Ações" to f.acoesTomadasImediato.ifBlank { "—" })
        )

        layout.drawSectionTitle("Descrição do Evento")
        layout.drawTable(
            listOf("Descrição" to f.descricaoEvento.ifBlank { "—" })
        )
        layout.drawFooter()

        f.fotosParaExportacao().forEach { (titulo, foto) ->
            val resolved = resolveFotoJpeg(foto)
            layout.drawAnexoFotoPage(
                secao = titulo,
                resposta = "—",
                foto = foto,
                formLat = f.latitude,
                formLng = f.longitude,
                resolved = resolved
            )
        }

        return assemblePdf(layout.finishPages(), layout.jpegs)
    }


    suspend fun buildAnaliseVisualNr(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.analiseVisualNr
            ?: br.com.htms.inspeccaocampo.data.nrvisual.FormAnaliseVisualNr()
        val layout = LayoutEngine()
        layout.setPageBackground(loadHeaderJpeg(PAGE_BG_ASSET), PAGE_BG_TOP_MARGIN)
        val logoJpeg = loadHeaderJpeg("Logo-HTMS-pdf.png")
            ?: loadHeaderJpeg("logo_htms.png")
        layout.drawHeader(logoJpeg, banner = null)
        layout.drawSectionTitle("Analise visual de cena (assistente NR)")
        layout.drawTable(listOf("Aviso" to br.com.htms.inspeccaocampo.data.nrvisual.NrVisualCatalog.DISCLAIMER_CURTO))
        layout.drawSectionTitle("Identificacao")
        layout.drawTable(buildList {
            add("TST / responsavel" to f.tstResponsavel.ifBlank { "-" })
            add("Obra" to f.obra.ifBlank { "-" })
            add("Cena" to f.cenaTipo.rotulo)
            add("Data/hora" to f.dataHorarioFormatado())
            add("Local (GPS)" to f.localTexto.replace('\n', ' ').replace('\r', ' ').ifBlank { "-" })
            f.linkGoogleMaps()?.let { add("Google Maps" to it) }
            add("Norma (versao)" to (f.normaVersao + " (" + f.normaVersaoData + ")"))
            add("Modo de analise" to f.modoAnalise.ifBlank { "-" })
            add("Objetos detectados" to f.objetosDetectados.joinToString(", ").ifBlank { "-" })
            add("Resumo da cena" to f.resumoCena.ifBlank { "-" })
        })
        layout.drawSectionTitle("Descricao da cena")
        layout.drawTable(listOf("Descricao" to f.descricaoCena.ifBlank { "-" }))
        val confirmados = f.achadosConfirmados()
        val observadosOk = f.achadosObservadosOk()
        if (observadosOk.isNotEmpty()) {
            layout.drawSectionTitle("Itens observados OK")
            layout.drawTable(observadosOk.map { a -> a.titulo to (a.nr + " | " + a.oQueViu.ifBlank { "-" }) })
        }
        layout.drawSectionTitle("Achados CONFIRMADOS (relatorio oficial)")
        if (confirmados.isEmpty()) {
            layout.drawTable(listOf("Resultado" to "Nenhum achado confirmado pelo TST. Isto nao significa ausencia de risco."))
        } else {
            layout.drawTable(confirmados.mapIndexed { i, a ->
                ((i + 1).toString() + ". " + a.titulo) to (a.nr + " | " + a.gravidade.rotulo + " | " + a.citar + " / Viu: " + a.oQueViu.ifBlank { "-" } + " / Acao: " + a.acaoSugerida.ifBlank { "-" })
            })
        }
        val outros = f.achados.filter { !it.entraNoRelatorioOficial }
        if (outros.isNotEmpty()) {
            layout.drawSectionTitle("Achados nao oficiais (descartados / N/A)")
            layout.drawTable(outros.map { a -> a.titulo to (a.status.rotulo + " | " + a.nr) })
        }
        layout.drawSectionTitle("Limitacoes da camera")
        layout.drawTable(listOf("Cobertura" to br.com.htms.inspeccaocampo.data.nrvisual.NrVisualCatalog.LIMITACOES_CAMERA))
        if (f.observacoesTst.isNotBlank()) {
            layout.drawSectionTitle("Observacoes do TST")
            layout.drawTable(listOf("Obs." to f.observacoesTst))
        }
        layout.drawFooter()
        f.fotos.forEachIndexed { index, foto ->
            val resolved = resolveFotoJpeg(foto)
            layout.drawAnexoFotoPage(
                secao = "Cena - Foto " + (index + 1),
                resposta = f.cenaTipo.rotulo,
                foto = foto,
                formLat = f.latitude,
                formLng = f.longitude,
                resolved = resolved
            )
        }
        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    suspend fun buildChecklistDiario(inspecao: InspecaoCompleta): ByteArray {
        val f = inspecao.checklistDiario
            ?: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario()
        return if (f.marcaEnum() == br.com.htms.inspeccaocampo.data.checklist.ChecklistDiarioMarca.AXIA ||
            inspecao.formTipo == FormTipo.CHECKLIST_DIARIO_AXIA
        ) {
            buildChecklistDiarioAxia(inspecao, f)
        } else {
            buildChecklistDiarioHtms(inspecao, f)
        }
    }

    /**
     * PDF HTMS alinhado ao [ChecklistPdfExporter] do APK:
     * capa → grade DESCRIÇÃO + dias 1..31 → legenda/NC/assinaturas → fotos.
     */
    private suspend fun buildChecklistDiarioHtms(
        @Suppress("UNUSED_PARAMETER") inspecao: InspecaoCompleta,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario
    ): ByteArray {
        val layout = LayoutEngine()
        layout.setPageBackground(
            loadHeaderJpeg(PAGE_BG_HTMS_CHECKLIST) ?: loadHeaderJpeg(PAGE_BG_ASSET)
        )
        val logoJpeg = loadHeaderJpeg("Logo-HTMS-pdf.png")
            ?: loadHeaderJpeg("logo_htms.png")
        desenharCapaChecklistDiarioHtms(layout, f, logoJpeg)
        desenharGradeChecklistDiarioHtms(layout, f)
        desenharRodapeChecklistDiarioHtms(layout, f)
        anexarFotosChecklistDiario(layout, f)
        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    private fun desenharCapaChecklistDiarioHtms(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        logoJpeg: Pair<ByteArray, Pair<Int, Int>>?
    ) {
        val m = CD_MARGIN
        val contentW = PAGE_W - m * 2
        val titulo = f.tipoTitulo.ifBlank { f.tipoNome }
            .ifBlank { "Formulário de Inspeção Diária de Segurança" }
        val rotuloCond = f.marcaEnum().rotuloCondutor(f.tipoId).uppercase()

        layout.setCursorY(PAGE_H - m)
        var top = layout.cursorY()
        val headerH = 56.0
        val logoW = 128.0
        val revW = 88.0
        val titleW = contentW - logoW - revW

        layout.strokeRect(m, top - headerH, contentW, headerH)
        layout.vLine(m + logoW, top - headerH, top)
        layout.vLine(m + logoW + titleW, top - headerH, top)

        logoJpeg?.let { (bytes, wh) ->
            val maxW = logoW - 12
            val maxH = headerH - 10
            val scale = min(maxW / wh.first, maxH / wh.second)
            val w = wh.first * scale
            val h = wh.second * scale
            val x = m + 6 + (maxW - w) / 2
            val yImg = top - 5 - h - (maxH - h) / 2
            layout.drawJpegAt(bytes, x, yImg, w, h)
        }

        val titleLines = wrap(titulo, titleW - 14, 9.5).take(3)
        val lineH = 11.5
        val titleBlockH = titleLines.size * lineH
        var ty = top - (headerH - titleBlockH) / 2 - 9.0
        titleLines.forEach { linha ->
            layout.textCentered(linha, m + logoW, m + logoW + titleW, ty, 9.5, bold = true)
            ty -= lineH
        }

        val revX = m + logoW + titleW
        layout.hLine(revX, top - headerH / 2, revX + revW)
        layout.textInCell("REV.: 00", revX + 6, top - headerH / 2, revW - 12, headerH / 2, 7.5)
        layout.textInCell("Página: 1 de 1", revX + 6, top - headerH, revW - 12, headerH / 2, 7.5)
        top -= headerH

        val infoH = 26.0
        val colW = contentW / 3.0
        layout.strokeRect(m, top - infoH, contentW, infoH)
        layout.vLine(m + colW, top - infoH, top)
        layout.vLine(m + colW * 2, top - infoH, top)
        layout.textInCell("Obra: ${f.obra}", m + 5, top - infoH, colW - 10, infoH, 8.5)
        layout.textInCell("Mês/Ano: ${f.mesAnoEfetivo()}", m + colW + 5, top - infoH, colW - 10, infoH, 8.5)
        layout.textInCell(
            "Nº de Chek List: ${f.numeroChecklist.ifBlank { "1" }}",
            m + colW * 2 + 5,
            top - infoH,
            colW - 10,
            infoH,
            8.5
        )
        top -= infoH

        val meio = contentW / 2.0
        val hdrH = 18.0
        layout.strokeRect(m, top - hdrH, meio, hdrH)
        layout.strokeRect(m + meio, top - hdrH, meio, hdrH)
        layout.textInCell(
            "IDENTIFICAÇÃO DO VEÍCULO/MÁQUINA/EQUIPAMENTO",
            m + 4, top - hdrH, meio - 8, hdrH, 7.5, bold = true
        )
        layout.textInCell(
            "IDENTIFICAÇÃO DO $rotuloCond",
            m + meio + 4, top - hdrH, meio - 8, hdrH, 7.5, bold = true
        )
        top -= hdrH

        val blocoH = 66.0
        val rowH = blocoH / 3.0
        // Caixa única + divisórias alinhadas (evita linhas “fora” do miolo).
        layout.strokeRect(m, top - blocoH, contentW, blocoH)
        layout.vLine(m + meio, top - blocoH, top)
        layout.hLine(m, top - rowH, m + contentW)
        layout.hLine(m, top - rowH * 2, m + contentW)
        layout.vLine(m + meio / 2, top - blocoH, top - rowH * 2)
        layout.vLine(m + meio + meio / 2, top - blocoH, top - rowH * 2)

        layout.textInCell("Proprietário: ${f.proprietario}", m + 5, top - rowH, meio - 10, rowH, 8.0)
        layout.textInCell("Modelo: ${f.modelo}", m + 5, top - rowH * 2, meio - 10, rowH, 8.0)
        layout.textInCell("Combustível: ${f.combustivel}", m + 5, top - blocoH, meio / 2 - 10, rowH, 7.5)
        layout.textInCell(
            "Placa/Identificação: ${f.placa}",
            m + meio / 2 + 5, top - blocoH, meio / 2 - 10, rowH, 7.5
        )
        layout.textInCell("Nome: ${f.motoristaNome}", m + meio + 5, top - rowH, meio - 10, rowH, 8.0)
        layout.textInCell("Função: ${f.motoristaFuncao}", m + meio + 5, top - rowH * 2, meio - 10, rowH, 8.0)
        layout.textInCell(
            "CNH/Curso: ${f.motoristaCnh}",
            m + meio + 5, top - blocoH, meio / 2 - 10, rowH, 7.5
        )
        layout.textInCell(
            "Validade: ${f.motoristaValidade}",
            m + meio + meio / 2 + 5, top - blocoH, meio / 2 - 10, rowH, 7.5
        )
        top -= blocoH

        val dataH = 24.0
        layout.strokeRect(m, top - dataH, contentW, dataH)
        layout.textInCell(
            "Data da inspeção: ${f.dataInspecao()}  ${f.horaInspecao()}".trim(),
            m + 6, top - dataH, contentW - 12, dataH, 9.0, bold = true
        )
        layout.setCursorY(top - dataH - 8)
        layout.newPage()
    }

    private fun desenharGradeChecklistDiarioHtms(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario
    ) {
        val m = CD_MARGIN
        val contentW = PAGE_W - m * 2
        val descW = 205.0
        val diaW = (contentW - descW) / 31.0
        val headerH = 16.0
        val lineH = 8.5
        val padX = 3.0
        val font = 6.5
        val fontTiny = 5.5
        val fontDia = 5.0
        val diaAtual = f.diaDoMes()

        fun desenharCabecalhoGrade(y0: Double): Double {
            layout.strokeRect(m, y0 - headerH, descW, headerH)
            layout.text("DESCRIÇÃO:", m + 4, y0 - 11, font, bold = true)
            for (d in 1..31) {
                val x = m + descW + (d - 1) * diaW
                layout.strokeRect(x, y0 - headerH, diaW, headerH)
                layout.withClip(x + 0.6, y0 - headerH + 0.6, diaW - 1.2, headerH - 1.2) {
                    layout.textCentered(d.toString(), x, x + diaW, y0 - 11, fontDia, bold = false)
                }
            }
            return y0 - headerH
        }

        layout.setCursorY(PAGE_H - m)
        var y = desenharCabecalhoGrade(layout.cursorY())

        f.itens.forEach { item ->
            val desc = "${item.numero}. ${item.titulo}"
            val linhasDesc = wrap(desc, descW - padX * 2, font)
            val precisaFoto = br.com.htms.inspeccaocampo.data.checklist.ChecklistFotoObservacoes
                .precisaMultiplasFotos(item.titulo)
            val linhasFoto = if (precisaFoto) {
                val orient = br.com.htms.inspeccaocampo.data.checklist.ChecklistItemDef(
                    numero = item.numero.toIntOrNull() ?: 0,
                    descricao = item.titulo
                ).orientacaoFoto()
                wrap("* $orient", descW - padX * 2, fontTiny)
            } else {
                emptyList()
            }
            val totalLinhas = (linhasDesc.size + linhasFoto.size).coerceAtLeast(1)
            val rowH = (totalLinhas * lineH + 6.0).coerceAtLeast(14.0)

            if (y - rowH < m + 20) {
                layout.newPage()
                layout.setCursorY(PAGE_H - m)
                y = desenharCabecalhoGrade(layout.cursorY())
            }

            layout.strokeRect(m, y - rowH, descW, rowH)
            var ty = y - lineH - 2
            linhasDesc.forEach { linha ->
                layout.text(linha, m + padX, ty, font, bold = false)
                ty -= lineH
            }
            linhasFoto.forEach { linha ->
                layout.text(linha, m + padX, ty, fontTiny, bold = false)
                ty -= lineH
            }

            for (d in 1..31) {
                val x = m + descW + (d - 1) * diaW
                layout.strokeRect(x, y - rowH, diaW, rowH)
                if (d == diaAtual) {
                    val codigo = item.resposta?.codigo.orEmpty()
                    if (codigo.isNotBlank()) {
                        val fs = if (codigo.length >= 2) fontDia else 5.4
                        layout.withClip(x + 0.55, y - rowH + 0.55, diaW - 1.1, rowH - 1.1) {
                            layout.textCentered(
                                codigo,
                                x + 0.4,
                                x + diaW - 0.4,
                                y - rowH / 2 - fs * 0.32,
                                fs,
                                bold = true
                            )
                        }
                    }
                }
            }
            y -= rowH
        }
        layout.setCursorY(y)
        layout.newPage()
    }

    private suspend fun desenharRodapeChecklistDiarioHtms(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario
    ) {
        val m = CD_MARGIN
        val contentW = PAGE_W - m * 2
        layout.setCursorY(PAGE_H - m)
        var y = layout.cursorY()

        layout.strokeRect(m, y - 16, contentW, 16.0)
        layout.text(
            "LEGENDA: C = CONFORME / NC = NÃO CONFORME / NA = NÃO SE APLICA",
            m + 6,
            y - 11,
            8.0,
            bold = true
        )
        y -= 24

        val colItem = 50.0
        val colDesc = contentW - colItem
        layout.strokeRect(m, y - 16, colItem, 16.0)
        layout.text("ITEM", m + 10, y - 11, 8.0, bold = true)
        layout.strokeRect(m + colItem, y - 16, colDesc, 16.0)
        layout.text("DESCRIÇÃO DA NÃO CONFORMIDADE", m + colItem + 6, y - 11, 8.0, bold = true)
        y -= 16

        val ncs = f.itens
            .filter { it.resposta == RespostaConformidade.NC && it.justificativa.isNotBlank() }
            .sortedBy { it.numero.toIntOrNull() ?: 0 }

        repeat(8) { i ->
            val par = ncs.getOrNull(i)
            layout.strokeRect(m, y - 20, colItem, 20.0)
            if (par != null) {
                layout.textInCell(par.numero, m + 4, y - 20, colItem - 8, 20.0, 8.0)
            }
            layout.strokeRect(m + colItem, y - 20, colDesc, 20.0)
            if (par != null) {
                layout.textInCell(par.justificativa, m + colItem + 4, y - 20, colDesc - 8, 20.0, 8.0)
            }
            y -= 20
        }
        y -= 12

        val sigBoxH = 108.0
        val sigGap = 6.0
        val sigW = (contentW - sigGap * 2) / 3.0
        val labelH = 16.0
        val inkH = sigBoxH - labelH - 8.0
        if (y - sigBoxH < m + 16) {
            layout.newPage()
            layout.setCursorY(PAGE_H - m)
            y = layout.cursorY()
        }
        val rotuloCond = br.com.htms.inspeccaocampo.data.checklist.ChecklistCatalog
            .rotuloAssinaturaCondutor(f.tipoId)
        val assinaturas = listOf(
            Triple(rotuloCond, f.assinaturaMotorista, m),
            Triple("ENCARREGADO", f.assinaturaEncarregado, m + sigW + sigGap),
            Triple("SESMT / TRANSPORTE", f.assinaturaSesmt, m + (sigW + sigGap) * 2)
        )
        for ((rotulo, token, x) in assinaturas) {
            // Sem fundo colorido — só borda.
            layout.strokeRect(x, y - sigBoxH, sigW, sigBoxH)
            if (token.isNotBlank()) {
                layout.drawSignatureInBox(
                    token,
                    x + 8,
                    y - sigBoxH + labelH + 4,
                    sigW - 16,
                    inkH,
                    inkOnly = true
                )
            }
            layout.textCentered(rotulo, x, x + sigW, y - sigBoxH + 11, 8.0, bold = true)
        }
        layout.setCursorY(y - sigBoxH - 8)
    }

    /**
     * PDF Axia alinhado ao formulário oficial MEV 3.26 (geometria + grade + clip de texto).
     */
    private suspend fun buildChecklistDiarioAxia(
        @Suppress("UNUSED_PARAMETER") inspecao: InspecaoCompleta,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario
    ): ByteArray {
        val layout = LayoutEngine()
        // JPEG oficial primeiro (já é DCTDecode; não cai no logo quadrado).
        val logoJpeg = loadHeaderJpeg("logo_axia_rounded.jpg")
            ?: loadHeaderJpeg("logo_axia_rounded.png")
        val iconJpeg = loadHeaderJpeg("icone_axia_cabecalho.png")
        val watermarkJpeg = loadHeaderJpeg("marcadagua_axia.png")
        iniciarPaginaAxia(layout, watermarkJpeg)
        desenharCapaChecklistDiarioAxia(layout, f, logoJpeg, iconJpeg)
        desenharGradeChecklistDiarioAxia(layout, f, watermarkJpeg)
        desenharRodapeChecklistDiarioAxia(layout, f, watermarkJpeg)
        anexarFotosChecklistDiarioAxia(layout, f, watermarkJpeg)
        return assemblePdf(layout.finishPages(), layout.jpegs)
    }

    private fun iniciarPaginaAxia(
        layout: LayoutEngine,
        watermarkJpeg: Pair<ByteArray, Pair<Int, Int>>?
    ) {
        layout.setCursorY(PAGE_H - AXIA_TOP_MARGIN)
        desenharMarcaDaguaAxia(layout, watermarkJpeg)
    }

    /** Posição/tamanho iguais ao AxiaExportLayout.desenharMarcaDaguaPdf (APK MEV 3.26). */
    private fun desenharMarcaDaguaAxia(
        layout: LayoutEngine,
        watermarkJpeg: Pair<ByteArray, Pair<Int, Int>>?
    ) {
        val (bytes, wh) = watermarkJpeg ?: return
        val maxW = PAGE_W * 0.20
        val maxH = PAGE_H * 0.18
        val scale = min(maxW / wh.first, maxH / wh.second)
        val w = wh.first * scale
        val h = wh.second * scale
        val x = (PAGE_W - w) / 2.0 + PAGE_W * 0.05
        // Canvas APK: yTopo = PAGE_H*0.52 - h/2 → PDF bottom = PAGE_H*0.48 - h/2
        val imgY = PAGE_H * 0.48 - h / 2.0
        layout.drawJpegMultiplyAt(bytes, x, imgY, w, h)
    }

    private data class AxiaCampoCabecalho(val rotulo: String, val valor: String)
    private data class AxiaLinhaCabecalho(
        val esquerda: AxiaCampoCabecalho,
        val direita: AxiaCampoCabecalho? = null
    )

    private fun linhasCabecalhoAxia(
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario
    ): List<AxiaLinhaCabecalho> {
        val data = "${f.dataInspecao()}  ${f.horaInspecao()}".trim()
        val localidade = f.localidadeMobilizacao.ifBlank { f.obra }
        val tipoVeiculo = f.tipoVeiculo.ifBlank { f.tipoNome }
        return listOf(
            AxiaLinhaCabecalho(AxiaCampoCabecalho("Fornecedor/Cliente:", f.fornecedorCliente)),
            AxiaLinhaCabecalho(AxiaCampoCabecalho("Preenchido por:", f.preenchidoPor)),
            AxiaLinhaCabecalho(AxiaCampoCabecalho("Data do preenchimento:", data)),
            AxiaLinhaCabecalho(
                AxiaCampoCabecalho("Contrato:", f.contrato),
                AxiaCampoCabecalho("Validade do Contrato:", f.validadeContrato)
            ),
            AxiaLinhaCabecalho(
                AxiaCampoCabecalho("Gestor do Contrato:", f.gestorContrato),
                AxiaCampoCabecalho("Localidade de mobilização:", localidade)
            ),
            AxiaLinhaCabecalho(
                AxiaCampoCabecalho("Área de acesso:", f.areaAcesso),
                AxiaCampoCabecalho("Placa/identificação:", f.placa)
            ),
            AxiaLinhaCabecalho(
                AxiaCampoCabecalho("Marca/modelo:", f.modelo),
                AxiaCampoCabecalho("Ano de Fabricação:", f.anoFabricacao)
            ),
            AxiaLinhaCabecalho(AxiaCampoCabecalho("Tipo de veículo:", tipoVeiculo))
        )
    }

    private fun approxTextWidth(texto: String, fontSize: Double): Double {
        // Helvetica: fator conservador (acentos/maiúsculas) para truncar antes de estourar a célula.
        var w = 0.0
        for (ch in texto) {
            w += when {
                ch == ' ' -> 0.28
                ch in "ilI.,:;!'|" -> 0.28
                ch in "mwMW@" -> 0.85
                ch.isUpperCase() -> 0.72
                else -> 0.55
            } * fontSize
        }
        return w
    }

    private fun truncarPorLargura(texto: String, maxW: Double, fontSize: Double): String {
        if (texto.isBlank() || maxW <= 2.0) return ""
        if (approxTextWidth(texto, fontSize) <= maxW) return texto
        val ellipsis = "..."
        if (approxTextWidth(ellipsis, fontSize) > maxW) return ""
        var fim = texto.length
        while (fim > 0 && approxTextWidth(texto.take(fim) + ellipsis, fontSize) > maxW) {
            fim--
        }
        return if (fim <= 0) ellipsis else texto.take(fim) + ellipsis
    }

    private fun quebrarPorLargura(texto: String, maxW: Double, fontSize: Double): List<String> {
        if (texto.isBlank()) return emptyList()
        val palavras = texto.split(Regex("\\s+"))
        val linhas = mutableListOf<String>()
        var atual = StringBuilder()
        palavras.forEach { p ->
            val candidato = if (atual.isEmpty()) p else "$atual $p"
            if (approxTextWidth(candidato, fontSize) <= maxW) {
                if (atual.isNotEmpty()) atual.append(' ')
                atual.append(p)
            } else {
                if (atual.isNotEmpty()) {
                    linhas += atual.toString()
                    atual = StringBuilder()
                }
                if (approxTextWidth(p, fontSize) <= maxW) {
                    atual.append(p)
                } else {
                    linhas += truncarPorLargura(p, maxW, fontSize)
                }
            }
        }
        if (atual.isNotEmpty()) linhas += atual.toString()
        return linhas.ifEmpty { listOf(texto) }
    }

    /** Grade Axia: linhas preenchidas (igual ao MEV 3.26) — a borda direita não some. */
    private fun LayoutEngine.gradeAxia(xs: DoubleArray, bottom: Double, h: Double) {
        val top = bottom + h
        val xL = xs.first()
        val xR = xs.last()
        fillHLineAxia(xL, xR, bottom)
        fillHLineAxia(xL, xR, top)
        xs.forEach { x -> fillVLineAxia(x, bottom, top) }
    }

    /**
     * Grade sem a borda vertical da extrema direita (marcação vermelha do usuário).
     * Mantém horizontais até [xs.last] e as verticais internas/esquerda.
     */
    private fun LayoutEngine.gradeAxiaSemBordaDireita(xs: DoubleArray, bottom: Double, h: Double) {
        val top = bottom + h
        val xL = xs.first()
        val xR = xs.last()
        fillHLineAxia(xL, xR, bottom)
        fillHLineAxia(xL, xR, top)
        xs.dropLast(1).forEach { x -> fillVLineAxia(x, bottom, top) }
    }

    private fun LayoutEngine.textoNaCelulaAxia(
        texto: String,
        x: Double,
        bottom: Double,
        w: Double,
        h: Double,
        fontSize: Double,
        bold: Boolean = false,
        maxLinhas: Int = 2,
        centralizado: Boolean = false
    ) {
        val maxW = (w - AXIA_PAD * 2).coerceAtLeast(2.0)
        if (texto.isBlank() || maxW <= 2.0 || h <= 2.0) return
        var linhas = quebrarPorLargura(texto, maxW, fontSize)
        if (linhas.size > maxLinhas) {
            linhas = linhas.take(maxLinhas).toMutableList()
            linhas[maxLinhas - 1] = truncarPorLargura(linhas[maxLinhas - 1], maxW, fontSize)
        } else {
            linhas = linhas.map { truncarPorLargura(it, maxW, fontSize) }
        }
        val padY = 2.6
        val lineH = fontSize + 2.2
        val top = bottom + h
        var ty = top - padY - fontSize * 0.82
        val minLast = bottom + padY + fontSize * 0.18
        val needed = (linhas.size - 1) * lineH
        if (ty - needed < minLast) {
            ty = minLast + needed
        }
        val maxFirst = top - padY - fontSize * 0.15
        if (ty > maxFirst) ty = maxFirst
        // Clip só horizontal — glifos (p/g/j) não são cortados pelas linhas da célula.
        withClipX(x + 0.6, w - 1.2) {
            linhas.forEach { linha ->
                if (centralizado) {
                    val tw = approxTextWidth(linha, fontSize)
                    val tx = x + ((w - tw) / 2.0).coerceAtLeast(AXIA_PAD * 0.5)
                    text(linha, tx, ty, fontSize, bold)
                } else {
                    text(linha, x + AXIA_PAD, ty, fontSize, bold)
                }
                ty -= lineH
            }
        }
    }

    private fun desenharCapaChecklistDiarioAxia(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        logoJpeg: Pair<ByteArray, Pair<Int, Int>>?,
        iconJpeg: Pair<ByteArray, Pair<Int, Int>>?
    ) {
        val x0 = AXIA_X0
        val contentW = AXIA_CONTENT_W
        val titulo = f.tipoTitulo.ifBlank { f.tipoNome }.ifBlank { "Check list Axia" }
        val fontCab = 6.0
        val fontTitle = 8.9

        var top = layout.cursorY()
        val headerH = AXIA_TITLE_H
        val logoW = AXIA_LOGO_W
        val logoX = AXIA_LOGO_X
        val logoH = AXIA_LOGO_H
        val logoY = top - AXIA_LOGO_TOP_INSET - logoH
        val iconX = AXIA_ICON_X
        val iconW = AXIA_ICON_W
        val iconH = AXIA_ICON_H
        val iconY = top - AXIA_ICON_TOP_INSET - iconH

        layout.strokeRectAxia(x0, top - headerH, contentW, headerH)

        // Ícone oficial MEV (PNG) — o asset antigo estava incompleto; vetor só como fallback.
        if (iconJpeg != null) {
            layout.drawJpegAt(iconJpeg.first, iconX, iconY, iconW, iconH)
        } else {
            layout.drawAxiaBackArrow(iconX, iconY, iconW, iconH)
        }

        val areaTituloLeft = iconX + iconW + 4.0
        val areaTituloW = logoX - areaTituloLeft - 4.0
        val titleLines = quebrarPorLargura(titulo, areaTituloW, fontTitle).take(3)
        val lineH = 10.5
        val titleBlockH = titleLines.size * lineH
        var ty = top - ((headerH - titleBlockH) / 2.0) - fontTitle * 0.85
        titleLines.forEach { linha ->
            val tw = approxTextWidth(linha, fontTitle)
            val tx = areaTituloLeft + ((areaTituloW - tw) / 2.0).coerceAtLeast(0.0)
            layout.text(linha, tx, ty, fontTitle, bold = true)
            ty -= lineH
        }

        logoJpeg?.let { (bytes, _) ->
            // JPEG oficial MEV já tem cantos arredondados; clip extra garante o raio
            // mesmo se o fallback for um asset quadrado.
            layout.drawJpegRoundedAt(bytes, logoX, logoY, logoW, logoH, radius = 4.2)
        }
        top -= headerH

        val rowH = AXIA_HEADER_ROW_H
        val linhas = linhasCabecalhoAxia(f)
        linhas.forEachIndexed { index, linha ->
            // Últimas linhas (Marca/Ano e Tipo): sem borda direita (marcação vermelha).
            val semDireita = index >= linhas.size - 2
            if (linha.direita == null) {
                val xs = doubleArrayOf(x0, AXIA_X_ROTULO, AXIA_X1)
                if (semDireita) {
                    layout.gradeAxiaSemBordaDireita(xs, top - rowH, rowH)
                } else {
                    layout.gradeAxia(xs, top - rowH, rowH)
                }
                layout.textoNaCelulaAxia(
                    linha.esquerda.rotulo, x0, top - rowH, AXIA_X_ROTULO - x0, rowH, fontCab, maxLinhas = 1
                )
                layout.textoNaCelulaAxia(
                    linha.esquerda.valor, AXIA_X_ROTULO, top - rowH, AXIA_X1 - AXIA_X_ROTULO, rowH, fontCab, maxLinhas = 1
                )
            } else {
                val xs = doubleArrayOf(x0, AXIA_X_ROTULO, AXIA_X_MEIO, AXIA_X_ROTULO_DIR, AXIA_X1)
                if (semDireita) {
                    layout.gradeAxiaSemBordaDireita(xs, top - rowH, rowH)
                } else {
                    layout.gradeAxia(xs, top - rowH, rowH)
                }
                layout.textoNaCelulaAxia(
                    linha.esquerda.rotulo, x0, top - rowH, AXIA_X_ROTULO - x0, rowH, fontCab, maxLinhas = 1
                )
                layout.textoNaCelulaAxia(
                    linha.esquerda.valor, AXIA_X_ROTULO, top - rowH, AXIA_X_MEIO - AXIA_X_ROTULO, rowH, fontCab, maxLinhas = 1
                )
                layout.textoNaCelulaAxia(
                    linha.direita.rotulo, AXIA_X_MEIO, top - rowH, AXIA_X_ROTULO_DIR - AXIA_X_MEIO, rowH, fontCab, maxLinhas = 1
                )
                layout.textoNaCelulaAxia(
                    linha.direita.valor, AXIA_X_ROTULO_DIR, top - rowH, AXIA_X1 - AXIA_X_ROTULO_DIR, rowH, fontCab, maxLinhas = 1
                )
            }
            top -= rowH
        }

        val legendaH = AXIA_LEGENDA_H
        // Vermelho = só a borda ESQUERDA da legenda fora. Horizontais + direita permanecem.
        layout.fillHLineAxia(x0, AXIA_X1, top)
        layout.fillHLineAxia(x0, AXIA_X1, top - legendaH)
        layout.fillVLineAxia(AXIA_X1, top - legendaH, top)
        val legenda = listOf(
            "LEGENDA:",
            "C: Significa \"conforme\" ou seja o equipamento está em condições de uso",
            "NC: Significa \"não conforme\", ou seja, o equipamento não está em condições de uso",
            "NA: Significa \"não aplicável\", ou seja, o item não se aplica a esse equipamento",
            "Obs: Particularidades importantes referente ao item"
        )
        var ly = top - 10.5
        layout.withClipX(x0 + 0.8, contentW - 1.6) {
            legenda.forEach { texto ->
                layout.text(
                    truncarPorLargura(texto, contentW - AXIA_PAD * 2, 6.0),
                    x0 + AXIA_PAD,
                    ly,
                    6.0,
                    bold = false
                )
                ly -= 9.2
            }
        }
        layout.setCursorY(top - legendaH)
    }

    private fun desenharGradeChecklistDiarioAxia(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        watermarkJpeg: Pair<ByteArray, Pair<Int, Int>>?
    ) {
        val x0 = AXIA_X0
        val contentW = AXIA_CONTENT_W
        val colItensW = AXIA_COL_ITENS_W
        val colCW = AXIA_COL_C_W
        val colNcW = AXIA_COL_NC_W
        val colNaW = AXIA_COL_NA_W
        val colObsW = AXIA_COL_OBS_W
        val descW = contentW - colItensW - colCW - colNcW - colNaW - colObsW
        val xItens = x0
        val xDesc = xItens + colItensW
        val xC = xDesc + descW
        val xNc = xC + colCW
        val xNa = xNc + colNcW
        val xObs = xNa + colNaW
        val xs = doubleArrayOf(xItens, xDesc, xC, xNc, xNa, xObs, AXIA_X1)
        val font = 5.2
        val fontHead = 5.0
        val headerRowH = AXIA_TABLE_HEADER_H
        val rowHBase = if (f.itens.size >= 30) AXIA_ITEM_ROW_H_COMPACT else AXIA_ITEM_ROW_H
        val rowHWrap = if (f.itens.size >= 30) AXIA_ITEM_ROW_H_WRAP_COMPACT else AXIA_ITEM_ROW_H_WRAP
        val bottomLimite = AXIA_TOP_MARGIN + 20.0

        fun desenharCabecalhoTabela(y0: Double): Double {
            layout.gradeAxia(xs, y0 - headerRowH, headerRowH)
            layout.textoNaCelulaAxia(
                "Itens", xItens, y0 - headerRowH, colItensW, headerRowH, fontHead, bold = true,
                maxLinhas = 1, centralizado = true
            )
            layout.textoNaCelulaAxia(
                "Itens a serem verificados", xDesc, y0 - headerRowH, descW, headerRowH, fontHead,
                bold = true, maxLinhas = 1, centralizado = true
            )
            layout.textoNaCelulaAxia(
                "C", xC, y0 - headerRowH, colCW, headerRowH, fontHead, bold = true,
                maxLinhas = 1, centralizado = true
            )
            layout.textoNaCelulaAxia(
                "NC", xNc, y0 - headerRowH, colNcW, headerRowH, fontHead, bold = true,
                maxLinhas = 1, centralizado = true
            )
            layout.textoNaCelulaAxia(
                "NA", xNa, y0 - headerRowH, colNaW, headerRowH, fontHead, bold = true,
                maxLinhas = 1, centralizado = true
            )
            layout.textoNaCelulaAxia(
                "OBS", xObs, y0 - headerRowH, colObsW, headerRowH, fontHead, bold = true,
                maxLinhas = 1, centralizado = true
            )
            return y0 - headerRowH
        }

        var y = desenharCabecalhoTabela(layout.cursorY())

        f.itens.forEach { item ->
            val obs = item.justificativa
            val linhasDesc = quebrarPorLargura(item.titulo, descW - AXIA_PAD * 2, font)
            val linhasObs = if (obs.isBlank()) emptyList() else quebrarPorLargura(obs, colObsW - AXIA_PAD * 2, font)
            val rowH = if (linhasDesc.size > 1 || linhasObs.size > 1) rowHWrap else rowHBase

            if (y - rowH < bottomLimite) {
                layout.newPage()
                iniciarPaginaAxia(layout, watermarkJpeg)
                y = desenharCabecalhoTabela(layout.cursorY())
            }

            layout.gradeAxia(xs, y - rowH, rowH)
            layout.textoNaCelulaAxia(
                item.numero.toString(), xItens, y - rowH, colItensW, rowH, font,
                maxLinhas = 1, centralizado = true
            )
            layout.textoNaCelulaAxia(
                item.titulo, xDesc, y - rowH, descW, rowH, font, bold = false, maxLinhas = 2
            )
            layout.textoNaCelulaAxia(obs, xObs, y - rowH, colObsW, rowH, font, maxLinhas = 2)

            when (item.resposta) {
                RespostaConformidade.C ->
                    layout.textoNaCelulaAxia(
                        "X", xC, y - rowH, colCW, rowH, 5.4, bold = true,
                        maxLinhas = 1, centralizado = true
                    )
                RespostaConformidade.NC ->
                    layout.textoNaCelulaAxia(
                        "X", xNc, y - rowH, colNcW, rowH, 5.4, bold = true,
                        maxLinhas = 1, centralizado = true
                    )
                RespostaConformidade.NA ->
                    layout.textoNaCelulaAxia(
                        "X", xNa, y - rowH, colNaW, rowH, 5.4, bold = true,
                        maxLinhas = 1, centralizado = true
                    )
                null -> Unit
            }
            y -= rowH
        }
        layout.setCursorY(y)
    }

    private suspend fun desenharRodapeChecklistDiarioAxia(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        watermarkJpeg: Pair<ByteArray, Pair<Int, Int>>?
    ) {
        val x0 = AXIA_X0
        val contentW = AXIA_CONTENT_W
        val obsH = AXIA_OBS_H
        val sigH = AXIA_SIG_H
        var y = layout.cursorY()
        if (y - obsH - sigH < AXIA_TOP_MARGIN + 16) {
            layout.newPage()
            iniciarPaginaAxia(layout, watermarkJpeg)
            y = layout.cursorY()
        }

        layout.gradeAxiaSemBordaDireita(
            doubleArrayOf(x0, x0 + AXIA_COL_ITENS_W, AXIA_X1),
            y - obsH,
            obsH
        )
        layout.withClipX(x0 + 0.5, AXIA_COL_ITENS_W - 1.0) {
            val p = 5.2
            val tw1 = approxTextWidth("Observações", p)
            val tw2 = approxTextWidth("Gerais:", p)
            layout.text("Observações", x0 + (AXIA_COL_ITENS_W - tw1) / 2.0, y - 10.0, p, bold = true)
            layout.text("Gerais:", x0 + (AXIA_COL_ITENS_W - tw2) / 2.0, y - 18.5, p, bold = true)
        }
        layout.textoNaCelulaAxia(
            f.observacoesGerais,
            x0 + AXIA_COL_ITENS_W,
            y - obsH,
            contentW - AXIA_COL_ITENS_W,
            obsH,
            6.5,
            maxLinhas = 2
        )
        y -= obsH

        val colAprov = AXIA_COL_APROV_W
        val colReprov = AXIA_COL_REPROV_W
        val colAssin = contentW - colAprov - colReprov
        val xReprov = x0 + colAprov
        val xAssin = xReprov + colReprov

        // Faixa única compacta: bordas externas + divisórias Aprovado | Reprovado | Assinatura.
        layout.strokeRectAxia(x0, y - sigH, contentW, sigH)
        layout.fillVLineAxia(xReprov, y, y - sigH)
        layout.fillVLineAxia(xAssin, y, y - sigH)

        val labelBaseline = y - 8.2
        val box = 9.0
        val boxBottom = y - sigH + (sigH - box) / 2.0
        layout.text("Aprovado:", x0 + AXIA_PAD, labelBaseline, 6.5, bold = false)
        layout.strokeRectAxia(x0 + 58, boxBottom, box, box)
        if (f.aprovado == true) {
            layout.textoNaCelulaAxia(
                "X", x0 + 58, boxBottom, box, box, 5.8, bold = false,
                maxLinhas = 1, centralizado = true
            )
        }

        layout.text("Reprovado:", xReprov + AXIA_PAD, labelBaseline, 6.5, bold = false)
        layout.strokeRectAxia(xReprov + 62, boxBottom, box, box)
        if (f.aprovado == false) {
            layout.textoNaCelulaAxia(
                "X", xReprov + 62, boxBottom, box, box, 5.8, bold = false,
                maxLinhas = 1, centralizado = true
            )
        }

        layout.withClip(xAssin + 0.4, y - sigH, colAssin - 0.8, sigH) {
            layout.text(
                truncarPorLargura(
                    "Assinatura do responsável pela vistoria:",
                    colAssin - AXIA_PAD * 2,
                    5.4
                ),
                xAssin + AXIA_PAD,
                labelBaseline,
                5.4,
                bold = false
            )
        }
        if (f.assinaturaMotorista.isNotBlank()) {
            layout.drawSignatureInBox(
                f.assinaturaMotorista,
                xAssin + AXIA_PAD,
                y - sigH + 0.5,
                colAssin - AXIA_PAD * 2,
                (sigH - 9.5).coerceAtLeast(6.0),
                inkOnly = true,
                fillFactor = 1.0,
                clipToBox = true
            )
        }

        val classTxt = ". Classificação: Pública"
        val approx = approxTextWidth(classTxt, 7.0)
        layout.textRgb(
            classTxt,
            (PAGE_W - approx) / 2.0,
            AXIA_CLASS_Y,
            7.0,
            bold = true,
            r = AXIA_CLASS_R,
            g = AXIA_CLASS_G,
            b = AXIA_CLASS_B
        )

        layout.setCursorY(y - sigH - 8)
    }

    /** Fotos no estilo AxiaPdfExporter (título + rótulo + imagem central; sem rodapé HTMS). */
    private suspend fun anexarFotosChecklistDiarioAxia(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        watermarkJpeg: Pair<ByteArray, Pair<Int, Int>>?
    ) {
        val m = CD_MARGIN
        suspend fun anexarGrupo(tituloSecao: String, fotos: List<br.com.htms.inspeccaocampo.data.FotoAnexo>) {
            fotos.forEachIndexed { index, foto ->
                val resolved = resolveFotoJpeg(foto)
                layout.newPage()
                iniciarPaginaAxia(layout, watermarkJpeg)
                var y = layout.cursorY()
                layout.text(
                    truncarPorLargura(tituloSecao, PAGE_W - m * 2, 10.0),
                    m,
                    y - 12,
                    10.0,
                    bold = true
                )
                y -= 22
                val rotulo = if (fotos.size > 1) "Foto ${index + 1}" else "Documento"
                layout.text(rotulo, m, y - 10, 10.0, bold = false)
                y -= 18
                if (resolved != null) {
                    val (jpeg, size) = resolved
                    val maxW = PAGE_W - m * 2
                    val maxH = (y - m).coerceAtLeast(80.0)
                    val escala = min(maxW / size.width, maxH / size.height)
                    val w = size.width * escala
                    val h = size.height * escala
                    val x = m + (maxW - w) / 2.0
                    val yImg = m + (maxH - h) / 2.0
                    layout.drawJpegAt(jpeg, x, yImg, w, h)
                } else {
                    layout.text(
                        "Foto anexada, mas bytes indisponíveis neste dispositivo.",
                        m,
                        y - 12,
                        9.0,
                        bold = false
                    )
                }
                layout.setCursorY(m)
            }
        }
        anexarGrupo("Anexos — Plano de manutenção", f.fotosPlanoManutencao)
        anexarGrupo("Anexos — Evidências de manutenção", f.fotosEvidenciasManutencao)
        anexarGrupo("Anexos — ${AxiaRequisitosMev.DOC_CRLV_ROTULO}", f.fotosCrlv)
        f.itens.forEach { item ->
            item.fotos.forEachIndexed { index, foto ->
                val resolved = resolveFotoJpeg(foto)
                layout.newPage()
                iniciarPaginaAxia(layout, watermarkJpeg)
                var y = layout.cursorY()
                val tituloSecao = "Anexos - Item ${item.numero}: ${item.titulo}"
                layout.text(
                    truncarPorLargura(tituloSecao, PAGE_W - m * 2, 10.0),
                    m,
                    y - 12,
                    10.0,
                    bold = true
                )
                y -= 22
                val rotulo = if (item.fotos.size > 1) "Foto ${index + 1}" else "Foto do item"
                layout.text(rotulo, m, y - 10, 10.0, bold = false)
                y -= 18
                if (resolved != null) {
                    val (jpeg, size) = resolved
                    val maxW = PAGE_W - m * 2
                    val maxH = (y - m).coerceAtLeast(80.0)
                    val escala = min(maxW / size.width, maxH / size.height)
                    val w = size.width * escala
                    val h = size.height * escala
                    val x = m + (maxW - w) / 2.0
                    val yImg = m + (maxH - h) / 2.0
                    layout.drawJpegAt(jpeg, x, yImg, w, h)
                } else {
                    layout.text(
                        "Foto anexada, mas bytes indisponíveis neste dispositivo.",
                        m,
                        y - 12,
                        9.0,
                        bold = false
                    )
                }
                layout.setCursorY(m)
            }
        }
        anexarGrupo("Anexos — Observações gerais", f.fotosObservacoes)
    }

    private suspend fun anexarFotosChecklistDiario(
        layout: LayoutEngine,
        f: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario
    ) {
        f.itens.forEach { item ->
            val resp = item.resposta?.rotulo ?: item.resposta?.codigo ?: "—"
            item.fotos.forEachIndexed { index, foto ->
                val resolved = resolveFotoJpeg(foto)
                layout.drawAnexoFotoPage(
                    secao = "${item.tituloNumerado} — Foto ${index + 1}",
                    resposta = resp,
                    foto = foto,
                    formLat = f.latitude,
                    formLng = f.longitude,
                    resolved = resolved
                )
            }
        }
    }

    private fun textoItemVetores(item: ItemConformidade): String = buildString {
        append(
            when (item.resposta) {
                RespostaConformidade.C -> "Conforme"
                RespostaConformidade.NC -> "Não conforme"
                RespostaConformidade.NA -> "N/A"
                null -> "-"
            }
        )
        if (item.resposta == RespostaConformidade.NA && item.justificativa.isNotBlank()) {
            append("\nJustificativa: ${item.justificativa}")
        }
        if (item.fotos.isNotEmpty()) append("\nFotos: ${item.fotos.size}")
    }

    private fun textoItemConformidade(
        item: ItemConformidade,
        usarSimNao: Boolean = false
    ): String = buildString {
        val respostaTxt = when {
            item.resposta == null -> "-"
            usarSimNao -> item.resposta.rotuloSimNao
            else -> item.resposta.codigo
        }
        append(respostaTxt)
        if (item.valorExtra.isNotBlank()) {
            val label = item.labelExtra.ifBlank { "Valor" }
            append("\n$label: ${item.valorExtra}")
        }
        if (item.resposta == RespostaConformidade.NC || item.resposta == RespostaConformidade.NA) {
            if (item.justificativa.isNotBlank()) {
                append("\nJustificativa: ${item.justificativa}")
            }
        }
    }

    private fun assinaturaTexto(nome: String, data: String): String = buildString {
        if (nome.isNotBlank()) append(nome)
        if (data.isNotBlank()) {
            if (isNotEmpty()) append(" — ")
            append(data)
        }
        if (isEmpty()) append("—")
    }

    /**
     * 1) Base64 embutido no form (Web)
     * 2) loadImageBytes (memória/IDB/R2)
     * 3) toJpegBytes com fallback para JPEG cru se reencode falhar
     */
    suspend fun resolveFotoJpeg(
        foto: br.com.htms.inspeccaocampo.data.FotoAnexo
    ): Pair<ByteArray, ImageSize>? {
        val raw = foto.conteudoBase64
            ?.takeIf { it.isNotBlank() }
            ?.let { PlatformServices.decodeBase64(it) }
            ?.takeIf { it.isNotEmpty() }
            ?: PlatformServices.loadImageBytes(foto.path)
            ?: foto.path.substringAfterLast('/').takeIf { it.isNotBlank() && it != foto.path }
                ?.let { name ->
                    PlatformServices.loadImageBytes(
                        br.com.htms.inspeccaocampo.platform.PlatformFiles.join(
                            br.com.htms.inspeccaocampo.platform.PlatformFiles.documentsDir(),
                            "fotos/$name"
                        )
                    )
                }
            ?: return null
        val jpeg = try {
            // Export: 1280px / q≈0.82 — nítido em A4, bem mais leve que 1600@0.9.
            PlatformImage.toJpegBytes(
                raw,
                quality = 0.82f,
                maxEdgePx = 1280,
                forceReencode = true
            )?.takeIf { isJpeg(it) }
        } catch (_: Throwable) {
            null
        } ?: raw.takeIf { isJpeg(it) }
            ?: return null
        // Dimensões: SOF → imageSize → fallback (melhor embutir distorcido que placeholder).
        val size = ImageHeaders.size(jpeg)
            ?: PlatformImage.imageSize(jpeg)
            ?: ImageSize(1024, 768)
        return jpeg to size
    }

    private suspend fun loadHeaderJpeg(
        assetName: String,
        flattenRgb: Int? = null
    ): Pair<ByteArray, Pair<Int, Int>>? {
        val raw = PlatformServices.loadBundledAsset(assetName) ?: return null
        val jpeg = PlatformImage.toJpegBytes(raw, flattenRgb = flattenRgb)?.takeIf { isJpeg(it) }
            ?: raw.takeIf { isJpeg(it) }
            ?: return null
        val size = ImageHeaders.size(jpeg)
            ?: PlatformImage.imageSize(jpeg)
            ?: ImageSize(100, 100)
        return jpeg to (size.width to size.height)
    }

    private fun isJpeg(bytes: ByteArray): Boolean =
        bytes.size > 2 && bytes[0] == 0xFF.toByte() && bytes[1] == 0xD8.toByte()

    private suspend fun signatureBytesToJpeg(raw: ByteArray): ByteArray? {
        try {
            PlatformImage.toSignatureJpegBytes(raw)?.takeIf { isJpeg(it) }?.let { return it }
        } catch (_: Throwable) {
        }
        try {
            PlatformImage.toJpegBytes(raw, flattenRgb = 0xFFFFFF)?.takeIf { isJpeg(it) }?.let { return it }
        } catch (_: Throwable) {
        }
        return raw.takeIf { isJpeg(it) }
    }

    private class LayoutEngine {
        val jpegs = mutableListOf<ByteArray>()
        private val pages = mutableListOf<PageDesc>()
        private val ops = StringBuilder()
        private val pageImageIdx = mutableListOf<Int>()
        private var topMargin = MARGIN
        private var y = PAGE_H - MARGIN
        /** Fundo de página (JPEG); esticado exatamente em PAGE_W×PAGE_H. */
        private var pageBackground: Pair<ByteArray, Pair<Int, Int>>? = null

        fun setPageBackground(bg: Pair<ByteArray, Pair<Int, Int>>?, topMargin: Double = MARGIN) {
            pageBackground = bg
            this.topMargin = topMargin
            if (ops.isEmpty() && pageImageIdx.isEmpty()) {
                y = PAGE_H - topMargin
                paintPageBackground()
            }
        }

        /**
         * Preenche a folha A4 inteira com o JPEG de fundo (stretch).
         * Sem crop e sem margens brancas — escala exata para PAGE_W × PAGE_H.
         */
        private fun paintPageBackground() {
            val bg = pageBackground ?: return
            val idx = addJpeg(bg.first)
            ops.append("q\n$PAGE_W 0 0 $PAGE_H 0 0 cm\n/Im${idx + 1} Do\nQ\n")
        }

        fun finishPages(): List<PageDesc> {
            flushPage()
            return pages.ifEmpty { listOf(PageDesc("")) }
        }

        fun newPage() {
            flushPage()
            y = PAGE_H - topMargin
            paintPageBackground()
        }

        private fun flushPage() {
            if (ops.isEmpty() && pageImageIdx.isEmpty()) return
            pages += PageDesc(ops.toString(), pageImageIdx.toList())
            ops.clear()
            pageImageIdx.clear()
        }

        private fun ensure(altura: Double) {
            if (y - altura < MARGIN + 28) newPage()
        }

        fun addJpeg(bytes: ByteArray): Int {
            // Reusa o mesmo XObject (fundo/logo/banner por referência) — evita
            // embutir o mesmo JPEG em cada página e inflar o PDF.
            val existing = jpegs.indexOfFirst { it === bytes }
            if (existing >= 0) {
                if (existing !in pageImageIdx) pageImageIdx += existing
                return existing
            }
            jpegs += bytes
            val idx = jpegs.lastIndex
            pageImageIdx += idx
            return idx
        }

        fun drawHeader(
            logo: Pair<ByteArray, Pair<Int, Int>>?,
            banner: Pair<ByteArray, Pair<Int, Int>>?
        ) {
            // Logo à esquerda + banner colado à direita (sem “slot” centralizado
            // que abre faixa branca entre os dois).
            // Sem logo (Inspeção de Campo): faixa curta no retângulo vermelho
            // (~55–65pt de altura; ~58pt mede ~70% do banner oversized a 82pt).
            val gap = 8.0
            val logoMaxW = CONTENT_W * 0.28
            val logoMaxH = 70.0
            val topoMaxH = if (logo == null) 58.0 else 78.0
            ensure(topoMaxH + 16)

            var logoH = 0.0
            var logoW = 0.0
            var topoH = 0.0
            val yTop = y

            logo?.let { (bytes, wh) ->
                val idx = addJpeg(bytes)
                val scale = min(logoMaxW / wh.first, logoMaxH / wh.second)
                val w = wh.first * scale
                val h = wh.second * scale
                val x = MARGIN
                val imgY = yTop - h
                ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
                logoH = h
                logoW = w
            }

            banner?.let { (bytes, wh) ->
                val idx = addJpeg(bytes)
                val topoLeft = if (logoW > 0) logoW + gap else 0.0
                val topoMaxW = (CONTENT_W - topoLeft).coerceAtLeast(1.0)
                // Sempre proporcional — nunca esticar só a largura e capar altura.
                val scale = min(topoMaxW / wh.first, topoMaxH / wh.second)
                val w = wh.first * scale
                val h = wh.second * scale
                // Sem logo: centraliza na área útil (CONTENT_W = PAGE_W − 2×MARGIN).
                val x = if (logoW > 0) {
                    MARGIN + topoLeft
                } else {
                    MARGIN + (topoMaxW - w) / 2.0
                }
                val imgY = yTop - h
                ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
                topoH = h
            }

            if (logo == null && banner == null) {
                // Fallback textual se assets não carregarem
                ops.append("BT\n/F2 14 Tf\n$MARGIN ${yTop - 14} Td\n(${pdfEscape("HTMS ENERGIA")}) Tj\nET\n")
                ops.append(
                    "BT\n/F1 11 Tf\n$MARGIN ${yTop - 32} Td\n" +
                        "(${pdfEscape("PROCEDIMENTO INSPEÇÃO DE CAMPO DIÁRIA")}) Tj\nET\n"
                )
                logoH = 40.0
            }

            y = yTop - max(logoH, topoH) - 16
        }

        fun drawSectionTitle(texto: String) {
            ensure(28.0)
            ops.append("BT\n/F2 12 Tf\n$MARGIN ${y - 12} Td\n(${pdfEscape(texto)}) Tj\nET\n")
            y -= 26
        }

        /** Tabela compacta de metadados da página de foto (sem título "Foto N"). */
        fun drawCompactMetaTable(linhas: List<Pair<String, String>>) {
            val col1W = CONTENT_W * 0.28
            val col2X = MARGIN + col1W
            val col2W = CONTENT_W - col1W
            val pad = 3.0
            val fontSize = 8.0
            val lineH = 9.5

            linhas.forEach { (campo, valor) ->
                val labelLines = wrap(campo, col1W - 10, fontSize)
                val valueLines = wrap(valor.ifBlank { "—" }, col2W - 10, fontSize)
                val rowH = pad * 2 + lineH * max(labelLines.size, valueLines.size)
                ensure(rowH + 1)

                val top = y
                val bottom = y - rowH
                ops.append("q\n0.5 w\n0.65 0.65 0.65 RG\n")
                ops.append("$MARGIN $bottom $CONTENT_W $rowH re\nS\n")
                ops.append("$col2X $bottom m\n$col2X $top l\nS\nQ\n")

                var ty = top - pad - fontSize
                labelLines.forEach { line ->
                    ops.append("BT\n/F2 $fontSize Tf\n${MARGIN + 4} $ty Td\n(${pdfEscape(line)}) Tj\nET\n")
                    ty -= lineH
                }
                ty = top - pad - fontSize
                valueLines.forEach { line ->
                    ops.append("BT\n/F1 $fontSize Tf\n${col2X + 4} $ty Td\n(${pdfEscape(line)}) Tj\nET\n")
                    ty -= lineH
                }
                y = bottom
            }
            y -= 4
        }

        fun drawTable(linhas: List<Pair<String, String>>) {
            val col1W = CONTENT_W * COL1_RATIO
            val col2X = MARGIN + col1W
            val col2W = CONTENT_W - col1W
            val pad = 6.0
            val fontSize = 9.0
            val lineH = 11.0

            linhas.forEach { (campo, valor) ->
                val labelLines = wrap(campo, col1W - 14, fontSize)
                val valueLines = wrap(valor.ifBlank { "—" }, col2W - 14, fontSize)
                val rowH = pad * 2 + lineH * max(labelLines.size, valueLines.size)
                ensure(rowH + 2)

                val top = y
                val bottom = y - rowH
                // Borda externa + divisor (cinza claro, como APK)
                ops.append("q\n0.6 w\n0.65 0.65 0.65 RG\n")
                ops.append("$MARGIN $bottom $CONTENT_W $rowH re\nS\n")
                ops.append("$col2X $bottom m\n$col2X $top l\nS\nQ\n")

                var ty = top - pad - fontSize
                labelLines.forEach { line ->
                    ops.append("BT\n/F2 $fontSize Tf\n${MARGIN + 6} $ty Td\n(${pdfEscape(line)}) Tj\nET\n")
                    ty -= lineH
                }
                ty = top - pad - fontSize
                val isLink = valor.startsWith("http")
                valueLines.forEach { line ->
                    if (isLink) {
                        ops.append("BT\n/F1 $fontSize Tf\n0 0 1 rg\n${col2X + 6} $ty Td\n(${pdfEscape(line)}) Tj\n0 g\nET\n")
                    } else {
                        ops.append("BT\n/F1 $fontSize Tf\n${col2X + 6} $ty Td\n(${pdfEscape(line)}) Tj\nET\n")
                    }
                    ty -= lineH
                }
                y = bottom
            }
            y -= 8
        }

        suspend fun drawSignature(rotulo: String, base64: String) {
            ensure(24.0)
            ops.append("BT\n/F2 11 Tf\n$MARGIN ${y - 11} Td\n(${pdfEscape(rotulo)}) Tj\nET\n")
            y -= 20
            if (base64.isBlank()) {
                y -= 8
                return
            }
            val raw = PlatformServices.decodeBase64(base64)
            if (raw == null || raw.isEmpty()) {
                ops.append(
                    "BT\n/F1 9 Tf\n0.55 0.1 0.1 rg\n$MARGIN ${y - 10} Td\n" +
                        "(Assinatura inválida — regenere o PDF no Histórico.) Tj\n0 g\nET\n"
                )
                y -= 18
                return
            }
            val jpeg = signatureBytesToJpeg(raw)
            if (jpeg == null) {
                ops.append(
                    "BT\n/F1 9 Tf\n0.55 0.1 0.1 rg\n$MARGIN ${y - 10} Td\n" +
                        "(Assinatura não renderizada — use Gerar PDF/Excel no admin.) Tj\n0 g\nET\n"
                )
                y -= 18
                return
            }
            val size = ImageHeaders.size(jpeg)
                ?: PlatformImage.imageSize(jpeg)
                ?: ImageSize(320, 120)
            val boxW = CONTENT_W * 0.55
            val boxH = 120.0
            // ensure ANTES de addJpeg — senão o XObject fica na página anterior sem /Do.
            ensure(boxH + 16)
            val idx = addJpeg(jpeg)
            val bottom = y - boxH
            ops.append("q\n0.8 w\n$MARGIN $bottom $boxW $boxH re\nS\nQ\n")
            val scale = min((boxW - 10) / size.width, (boxH - 10) / size.height)
            val w = size.width * scale
            val h = size.height * scale
            val x = MARGIN + (boxW - w) / 2
            val imgY = bottom + (boxH - h) / 2
            // Sem Multiply: no portal o blend sumia a tinta sobre o fundo A4.
            // JPEG já vem com fundo branco (signatureBytesToJpeg).
            ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
            y = bottom - 14
        }

        /**
         * Embutir na página atual, escalando ao espaço restante.
         *
         * Regras críticas (bug 2026-07-23 — foto no Excel, invisível no PDF):
         * 1) Nunca [ensure]/newPage] DEPOIS de [addJpeg]: o XObject ficava na
         *    página de metadados e o `/Im Do` na seguinte, sem Resources.
         * 2) Se o espaço restante for apertado para um retrato, [newPage] ANTES
         *    de [addJpeg] — Do e Resources na mesma página, foto maior.
         * 3) [assemblePdf] ainda faz merge dos `/ImN Do` do content → Resources
         *    (rede de segurança se o tracking de pageImageIdx falhar).
         */
        fun drawPhoto(jpeg: ByteArray, imgW: Int, imgH: Int) {
            drawPhotoFill(jpeg, imgW, imgH, enlarge = false)
        }

        /**
         * Foto ocupa quase todo o espaço restante da página (contain).
         * [enlarge]=true permite ampliar além do tamanho nativo para preencher a área.
         */
        fun drawPhotoFill(
            jpeg: ByteArray,
            imgW: Int,
            imgH: Int,
            enlarge: Boolean = true
        ) {
            val footerReserve = 26.0
            val maxW = CONTENT_W
            val maxH = (y - MARGIN - footerReserve).coerceAtLeast(120.0)
            var scale = minOf(maxW / imgW.toDouble(), maxH / imgH.toDouble())
            if (!enlarge) scale = minOf(scale, 1.0)
            val w = imgW * scale
            val h = imgH * scale
            val idx = addJpeg(jpeg)
            val x = MARGIN + (CONTENT_W - w) / 2
            val imgY = y - h
            ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
            y = imgY - 6
        }

        /** Página de anexo: meta compacta + foto em quase toda a folha. */
        fun drawAnexoFotoPage(
            secao: String,
            resposta: String,
            foto: FotoAnexo,
            formLat: Double?,
            formLng: Double?,
            resolved: Pair<ByteArray, ImageSize>?
        ) {
            newPage()
            drawCompactMetaTable(
                listOf(
                    "Seção" to secao,
                    "Resposta" to resposta.ifBlank { "—" },
                    "Data/Hora" to formatDateTimeBr(foto.capturadoEm),
                    "GPS da foto" to when {
                        foto.latitude != null && foto.longitude != null ->
                            "${foto.latitude}, ${foto.longitude}"
                        formLat != null && formLng != null ->
                            "$formLat, $formLng"
                        else -> "GPS não capturado"
                    }
                )
            )
            if (resolved != null) {
                drawPhotoFill(
                    resolved.first,
                    resolved.second.width,
                    resolved.second.height,
                    enlarge = true
                )
            } else {
                drawCompactMetaTable(
                    listOf(
                        "Status" to
                            "Foto anexada no formulário, mas os bytes não puderam ser " +
                            "carregados neste dispositivo. Tire a foto novamente ou " +
                            "exporte com a mesma sessão do navegador (sem limpar dados).",
                        "Path" to foto.path.take(120)
                    )
                )
            }
            drawFooter()
        }

        fun drawFooter() {
            val t = pdfEscape("HTMS ENERGIA — Inspeção de Campo Diária")
            ops.append("BT\n/F1 8 Tf\n$MARGIN 22 Td\n($t) Tj\nET\n")
        }

        fun cursorY(): Double = y

        fun setCursorY(value: Double) {
            y = value
        }

        /** Retângulo com borda; [bottom] é a coordenada Y inferior (PDF). */
        fun strokeRect(x: Double, bottom: Double, w: Double, h: Double) {
            ops.append("q\n1 w\n0 0 0 RG\n$x $bottom $w $h re\nS\nQ\n")
        }

        fun strokeRectAxia(x: Double, bottom: Double, w: Double, h: Double) {
            val x1 = x + w
            val y1 = bottom + h
            fillHLineAxia(x, x1, bottom)
            fillHLineAxia(x, x1, y1)
            fillVLineAxia(x, bottom, y1)
            fillVLineAxia(x1, bottom, y1)
        }

        fun vLine(x: Double, y1: Double, y2: Double) {
            ops.append("q\n1 w\n0 0 0 RG\n$x $y1 m\n$x $y2 l\nS\nQ\n")
        }

        fun vLineAxia(x: Double, y1: Double, y2: Double) {
            fillVLineAxia(x, y1, y2)
        }

        fun hLine(x1: Double, yLine: Double, x2: Double) {
            ops.append("q\n1 w\n0 0 0 RG\n$x1 $yLine m\n$x2 $yLine l\nS\nQ\n")
        }

        /** Linha horizontal preenchida (MEV 3.26) — não some no zoom/impressão. */
        fun fillHLineAxia(x1: Double, x2: Double, y: Double) {
            val t = AXIA_LINE
            val left = min(x1, x2)
            val w = abs(x2 - x1)
            ops.append("q\n0 0 0 rg\n$left ${y - t / 2.0} $w $t re\nf\nQ\n")
        }

        /** Linha vertical preenchida, inclusive a borda direita da grade. */
        fun fillVLineAxia(x: Double, y1: Double, y2: Double) {
            val t = AXIA_LINE
            val bottom = min(y1, y2)
            val h = abs(y2 - y1)
            ops.append("q\n0 0 0 rg\n${x - t / 2.0} $bottom $t $h re\nf\nQ\n")
        }

        /** Clip PDF: evita texto atravessar linhas/colunas vizinhas. */
        inline fun withClip(x: Double, bottom: Double, w: Double, h: Double, block: () -> Unit) {
            ops.append("q\n$x $bottom $w $h re\nW\nn\n")
            block()
            ops.append("Q\n")
        }

        /** Clip só nas laterais (altura da página) — não corta ascenders/descenders. */
        inline fun withClipX(x: Double, w: Double, block: () -> Unit) {
            ops.append("q\n$x 0 $w $PAGE_H re\nW\nn\n")
            block()
            ops.append("Q\n")
        }

        /** Seta de voltar (reply/U-turn) — fallback se o PNG oficial não carregar. */
        fun drawAxiaBackArrow(x: Double, y: Double, w: Double, h: Double) {
            val pad = 1.15
            val x0 = x + pad
            val y0 = y + pad
            val x1 = x + w - pad
            val y1 = y + h - pad
            ops.append("q\n$AXIA_LINE w\n0 0 0 RG\n0 0 0 rg\n")
            // Caixa do canto (como no MEV).
            ops.append("$x0 $y0 m\n$x1 $y0 l\n$x1 $y1 l\n$x0 $y1 l\nh\nS\n")
            val cx = x + w * 0.52
            val cy = y + h * 0.50
            val r = min(w, h) * 0.28
            val sw = min(w, h) * 0.095
            ops.append("$sw w\n1 J\n1 j\n")
            // Arco direito (U) + haste horizontal à esquerda.
            val headX = x + w * 0.22
            val shaftY = cy + r * 0.55
            val tailY = cy - r * 0.55
            ops.append("${headX + r * 0.15} $shaftY m\n")
            ops.append("${cx + r} $shaftY l\n")
            ops.append("${cx + r} $shaftY ${cx + r} $tailY $cx $tailY c\n")
            ops.append("S\n")
            // Cabeça da seta (triângulo à esquerda).
            val ah = min(w, h) * 0.18
            ops.append("0 0 0 rg\n")
            ops.append("$headX $shaftY m\n")
            ops.append("${headX + ah * 1.15} ${shaftY + ah * 0.85} l\n")
            ops.append("${headX + ah * 1.15} ${shaftY - ah * 0.85} l\n")
            ops.append("h\nf\n")
            // Cauda afilada (ponta inferior do U).
            ops.append("$sw w\n")
            ops.append("$cx $tailY m\n")
            ops.append("${cx - r * 0.55} $tailY l\n")
            ops.append("S\nQ\n")
        }

        fun fillGray(x: Double, bottom: Double, w: Double, h: Double) {
            ops.append("q\n0.75 0.75 0.75 rg\n$x $bottom $w $h re\nf\nQ\n")
            strokeRect(x, bottom, w, h)
        }

        fun fillYellow(x: Double, bottom: Double, w: Double, h: Double) {
            ops.append("q\n1 0.92 0.23 rg\n$x $bottom $w $h re\nf\nQ\n")
            strokeRect(x, bottom, w, h)
        }

        fun text(texto: String, x: Double, baselineY: Double, fontSize: Double, bold: Boolean) {
            val font = if (bold) "/F2" else "/F1"
            ops.append(
                "BT\n$font $fontSize Tf\n$x $baselineY Td\n(${pdfEscape(texto)}) Tj\nET\n"
            )
        }

        fun textRgb(
            texto: String,
            x: Double,
            baselineY: Double,
            fontSize: Double,
            bold: Boolean,
            r: Double,
            g: Double,
            b: Double
        ) {
            val font = if (bold) "/F2" else "/F1"
            ops.append(
                "q\n$r $g $b rg\nBT\n$font $fontSize Tf\n$x $baselineY Td\n(${pdfEscape(texto)}) Tj\nET\nQ\n"
            )
        }

        fun textCentered(
            texto: String,
            xLeft: Double,
            xRight: Double,
            baselineY: Double,
            fontSize: Double,
            bold: Boolean
        ) {
            val factor = if (bold) 0.62 else 0.50
            val approx = texto.length * fontSize * factor
            val span = (xRight - xLeft).coerceAtLeast(1.0)
            val x = xLeft + ((span - approx) / 2.0).coerceAtLeast(0.4)
            text(texto, x.coerceIn(xLeft, xRight - 0.5), baselineY, fontSize, bold)
        }

        fun textInCell(
            texto: String,
            x: Double,
            bottom: Double,
            maxW: Double,
            cellH: Double,
            fontSize: Double,
            bold: Boolean = false
        ) {
            var fs = fontSize
            var linhas = wrap(texto, maxW, fs)
            val maxLinesByHeight = { size: Double ->
                ((cellH - 6.0) / (size + 2.2)).toInt().coerceAtLeast(1)
            }
            while (linhas.size > maxLinesByHeight(fs) && fs > 6.0) {
                fs -= 0.5
                linhas = wrap(texto, maxW, fs)
            }
            linhas = linhas.take(maxLinesByHeight(fs))
            val lineH = fs + 2.2
            val totalH = linhas.size * lineH
            val top = bottom + cellH
            val pad = max(3.5, (cellH - totalH) / 2.0)
            var ty = top - pad - fs * 0.85
            if (ty - (linhas.size - 1) * lineH < bottom + 2.0) {
                ty = top - 3.5 - fs * 0.85
            }
            linhas.forEach { linha ->
                text(linha, x, ty, fs, bold)
                ty -= lineH
            }
        }

        fun drawJpegAt(bytes: ByteArray, x: Double, imgY: Double, w: Double, h: Double) {
            val idx = addJpeg(bytes)
            ops.append("q\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
        }

        /** JPEG com cantos arredondados via clip (modelo Axia). */
        fun drawJpegRoundedAt(
            bytes: ByteArray,
            x: Double,
            imgY: Double,
            w: Double,
            h: Double,
            radius: Double
        ) {
            val idx = addJpeg(bytes)
            val r = radius.coerceIn(0.5, min(w, h) / 2.0)
            val k = 0.5522847498 * r
            val x0 = x
            val y0 = imgY
            val x1 = x + w
            val y1 = imgY + h
            ops.append("q\n")
            ops.append("${x0 + r} $y0 m\n")
            ops.append("${x1 - r} $y0 l\n")
            ops.append("${x1 - r + k} $y0 $x1 ${y0 + r - k} $x1 ${y0 + r} c\n")
            ops.append("$x1 ${y1 - r} l\n")
            ops.append("$x1 ${y1 - r + k} ${x1 - r + k} $y1 ${x1 - r} $y1 c\n")
            ops.append("${x0 + r} $y1 l\n")
            ops.append("${x0 + r - k} $y1 $x0 ${y1 - r + k} $x0 ${y1 - r} c\n")
            ops.append("$x0 ${y0 + r} l\n")
            ops.append("$x0 ${y0 + r - k} ${x0 + r - k} $y0 ${x0 + r} $y0 c\n")
            ops.append("h\nW\nn\n")
            ops.append("$w 0 0 $h $x0 $y0 cm\n/Im${idx + 1} Do\nQ\n")
        }

        /** JPEG com blend Multiply (branco some) — marca d'água / ícone Axia. */
        fun drawJpegMultiplyAt(bytes: ByteArray, x: Double, imgY: Double, w: Double, h: Double) {
            val idx = addJpeg(bytes)
            ops.append("q\n/GSm gs\n$w 0 0 $h $x $imgY cm\n/Im${idx + 1} Do\nQ\n")
        }

        suspend fun drawSignatureInBox(
            base64: String,
            x: Double,
            bottom: Double,
            boxW: Double,
            boxH: Double,
            inkOnly: Boolean = false,
            fillFactor: Double = 1.0,
            clipToBox: Boolean = false
        ) {
            if (base64.isBlank()) return
            val raw = PlatformServices.decodeBase64(base64) ?: return
            val jpeg = signatureBytesToJpeg(raw) ?: return
            val size = ImageHeaders.size(jpeg) ?: return
            val pad = if (fillFactor > 1.0) -2.0 else 2.0
            val scale = min((boxW - pad * 2) / size.width, (boxH - pad * 2) / size.height) * fillFactor
            val w = size.width * scale
            val h = size.height * scale
            val ix = x + (boxW - w) / 2
            val iy = bottom + (boxH - h) / 2
            val idx = addJpeg(jpeg)
            if (clipToBox) {
                ops.append("q\n$x $bottom $boxW $boxH re\nW\nn\n")
            }
            // inkOnly também sem Multiply no web — senão a assinatura some no fundo.
            ops.append("q\n$w 0 0 $h $ix $iy cm\n/Im${idx + 1} Do\nQ\n")
            if (clipToBox) {
                ops.append("Q\n")
            }
        }
    }

    private fun wrap(text: String, maxWidth: Double, fontSize: Double): List<String> {
        val clean = text.replace('\r', ' ')
        // Helvetica CAPS/bold é mais largo que 0.5em — usar fator conservador.
        val avg = fontSize * 0.58
        val maxChars = (maxWidth / avg).toInt().coerceAtLeast(8)
        val out = mutableListOf<String>()
        clean.split('\n').forEach { paragraph ->
            if (paragraph.isBlank()) {
                out += ""
                return@forEach
            }
            var remaining = paragraph.trim()
            while (remaining.isNotEmpty()) {
                if (remaining.length <= maxChars) {
                    out += remaining
                    break
                }
                var cut = remaining.lastIndexOf(' ', maxChars)
                if (cut < maxChars / 3) cut = maxChars
                out += remaining.take(cut).trimEnd()
                remaining = remaining.drop(cut).trimStart()
            }
        }
        return out.ifEmpty { listOf("") }
    }

    private fun assemblePdf(pageDescs: List<PageDesc>, jpegPayloads: List<ByteArray>): ByteArray {
        val pages = pageDescs.ifEmpty { listOf(PageDesc("")) }
        val totalPages = pages.size
        val fontRegular = 3
        val fontBold = 4
        val firstImageNum = 5
        val firstPageNum = firstImageNum + jpegPayloads.size

        fun pageObjNum(i: Int) = firstPageNum + i * 2
        fun contentObjNum(i: Int) = pageObjNum(i) + 1
        fun imageObjNum(i: Int) = firstImageNum + i

        val kids = (0 until totalPages).joinToString(" ") { "${pageObjNum(it)} 0 R" }
        val outObjs = linkedMapOf<Int, ByteArray>()
        outObjs[1] = latin1("<< /Type /Catalog /Pages 2 0 R >>")
        outObjs[2] = latin1("<< /Type /Pages /Kids [ $kids ] /Count $totalPages >>")
        outObjs[fontRegular] = latin1(
            "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>"
        )
        outObjs[fontBold] = latin1(
            "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>"
        )

        jpegPayloads.forEachIndexed { idx, jpeg ->
            val size = ImageHeaders.size(jpeg)!!
            val dict = latin1(
                "<< /Type /XObject /Subtype /Image " +
                    "/Width ${size.width} /Height ${size.height} " +
                    "/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode " +
                    "/Length ${jpeg.size} >>\nstream\n"
            )
            outObjs[imageObjNum(idx)] = dict + jpeg + latin1("\nendstream")
        }

        pages.forEachIndexed { i, desc ->
            val pNum = pageObjNum(i)
            val cNum = contentObjNum(i)
            // Rede de segurança: Resources deve incluir TODO /ImN Do do stream,
            // não só o que pageImageIdx rastreou (evita foto “fantasma”).
            val indices = (desc.imageIndices + imageIndicesFromContent(desc.contentStream))
                .distinct()
                .filter { it in jpegPayloads.indices }
            val xobjects = if (indices.isEmpty()) {
                ""
            } else {
                indices.joinToString(" ") { imgIdx ->
                    "/Im${imgIdx + 1} ${imageObjNum(imgIdx)} 0 R"
                }.let { "/XObject << $it >> " }
            }
            val resources =
                "<< /Font << /F1 $fontRegular 0 R /F2 $fontBold 0 R >> " +
                    "/ExtGState << /GSm << /Type /ExtGState /BM /Multiply >> >> " +
                    "$xobjects>>"
            outObjs[pNum] = latin1(
                "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W.toInt()} ${PAGE_H.toInt()}] " +
                    "/Contents $cNum 0 R /Resources $resources >>"
            )
            val streamBytes = latin1(desc.contentStream)
            outObjs[cNum] = latin1("<< /Length ${streamBytes.size} >>\nstream\n") +
                streamBytes +
                latin1("\nendstream")
        }

        // Info dict — permite verificar no PDF qual build gerou (vs cache antigo).
        val infoNum = firstPageNum + totalPages * 2
        outObjs[infoNum] = latin1(
            "<< /Producer (${pdfEscape("ICD-JpegPdfBuilder $PDF_BUILD_ID")}) " +
                "/Creator (${pdfEscape("InspecaoCampoDiaria")}) >>"
        )

        return writePdf(outObjs, infoNum)
    }

    private fun writePdf(objs: Map<Int, ByteArray>, infoObjNum: Int? = null): ByteArray {
        val maxObj = objs.keys.maxOrNull() ?: 0
        val body = ArrayList<Byte>()
        body += latin1("%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n").toList()
        val offsets = IntArray(maxObj + 1)
        for (n in 1..maxObj) {
            offsets[n] = body.size
            val content = objs[n] ?: latin1("<< >>")
            body += latin1("$n 0 obj\n").toList()
            body += content.toList()
            body += latin1("\nendobj\n").toList()
        }
        val xrefPos = body.size
        val xref = StringBuilder()
        xref.append("xref\n0 ${maxObj + 1}\n")
        xref.append("0000000000 65535 f \n")
        for (n in 1..maxObj) {
            xref.append(offsets[n].toString().padStart(10, '0')).append(" 00000 n \n")
        }
        val infoRef = if (infoObjNum != null) " /Info $infoObjNum 0 R" else ""
        xref.append("trailer\n<< /Size ${maxObj + 1} /Root 1 0 R$infoRef >>\n")
        xref.append("startxref\n").append(xrefPos).append("\n%%EOF")
        body += latin1(xref.toString()).toList()
        return body.toByteArray()
    }

    private fun pdfEscape(s: String): String = buildString(s.length + 8) {
        for (ch in s) {
            val c = toWinAnsi(ch)
            when (c) {
                '\\'.code -> append("\\\\")
                '('.code -> append("\\(")
                ')'.code -> append("\\)")
                in 32..126 -> append(c.toChar())
                in 128..255 -> append("\\").append(c.toString(8).padStart(3, '0'))
                else -> append('?')
            }
        }
    }

    private fun toWinAnsi(ch: Char): Int {
        val code = ch.code
        if (code in 0..255) return code
        return when (ch) {
            '–', '—' -> '-'.code
            '“', '”', '„' -> '"'.code
            '‘', '’' -> '\''.code
            '…' -> '.'.code
            else -> '?'.code
        }
    }

    private fun latin1(s: String): ByteArray =
        ByteArray(s.length) { i ->
            val c = s[i].code
            (if (c in 0..255) c else '?'.code).toByte()
        }
}
